import React, { useState, useEffect, useRef, createContext, useContext } from 'react';

// ── Auth Context ──────────────────────────────────────────────────────────────

const AuthContext = createContext(null);

export function useAuth() { return useContext(AuthContext); }

function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem('adtsync_token'));
  const [username, setUsername] = useState(() => localStorage.getItem('adtsync_user'));

  const login = (tok, user) => {
    setToken(tok);
    setUsername(user);
    localStorage.setItem('adtsync_token', tok);
    localStorage.setItem('adtsync_user', user);
  };

  const logout = () => {
    setToken(null);
    setUsername(null);
    localStorage.removeItem('adtsync_token');
    localStorage.removeItem('adtsync_user');
  };

  return (
    <AuthContext.Provider value={{ token, username, login, logout, isAuthed: !!token }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── API Helper ────────────────────────────────────────────────────────────────

export function useApi() {
  const { token, logout } = useAuth();

  const call = async (method, path, body) => {
    const res = await fetch(`/api${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    if (res.status === 401) { logout(); return null; }
    if (!res.ok) throw new Error(await res.text());
    if (res.status === 204) return null;
    const text = await res.text();
    if (!text || text.trim() === '') return null;
    return JSON.parse(text);
  };

  return {
    get: (path) => call('GET', path),
    post: (path, body) => call('POST', path, body),
    put: (path, body) => call('PUT', path, body),
    patch: (path, body) => call('PATCH', path, body),
    del: (path) => call('DELETE', path),
  };
}

// ── Login Page ────────────────────────────────────────────────────────────────

function LoginPage() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      if (!res.ok) { setError('Invalid credentials.'); return; }
      const data = await res.json();
      login(data.token, data.username);
    } catch (e) {
      setError('Login failed. Server unreachable.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.loginWrap}>
      <div style={styles.loginCard}>
        <img src="https://advdigitech.com/wp-content/uploads/2021/04/ADT-logo-black.png"
             alt="ADT Logo" style={styles.loginLogo} />
        <h2 style={styles.loginTitle}>ADTSync Admin</h2>
        <form onSubmit={handleSubmit}>
          <input style={styles.input} placeholder="Username" value={username}
                 onChange={e => setUsername(e.target.value)} required />
          <input style={styles.input} type="password" placeholder="Password"
                 value={password} onChange={e => setPassword(e.target.value)} required />
          {error && <div style={styles.error}>{error}</div>}
          <button style={styles.btn} type="submit" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Shell / Nav ───────────────────────────────────────────────────────────────

function Shell({ children, page, setPage }) {
  const { username, logout } = useAuth();
  const nav = [
    { id: 'dashboard', label: 'Dashboard',    icon: 'ti-layout-dashboard' },
    { id: 'clients',   label: 'Clients',      icon: 'ti-users' },
    { id: 'files',     label: 'File Manager', icon: 'ti-folder' },
    { id: 'settings',  label: 'Settings',     icon: 'ti-settings' },
    { id: 'users',     label: 'Users',        icon: 'ti-user' },
    { id: 'agents',    label: 'Agents',       icon: 'ti-device-desktop' },
    { id: 'updates',   label: 'Updates',      icon: 'ti-refresh' },
  ];

  return (
    <div style={styles.shell}>
      <div style={styles.sidebar}>
        <img src="https://advdigitech.com/wp-content/uploads/2021/04/ADT-logo-black.png"
             alt="ADT" style={styles.sidebarLogo} />
        <div style={styles.sidebarTitle}>ADTSync</div>
        {nav.map(n => (
          <div key={n.id}
               style={{ ...styles.navItem, ...(page === n.id ? styles.navActive : {}) }}
               onClick={() => setPage(n.id)}>
            <i className={`ti ${n.icon}`} style={{fontSize:16, marginRight:10, verticalAlign:-2}} aria-hidden="true"></i>
            {n.label}
          </div>
        ))}
        <div style={styles.sidebarFooter}>
          <div style={styles.navUser}>
            <i className="ti ti-user-circle" style={{fontSize:15, marginRight:6, verticalAlign:-2}} aria-hidden="true"></i>
            {username}
          </div>
          <div style={{ ...styles.navItem, color: '#e07070' }} onClick={logout}>
            <i className="ti ti-logout" style={{fontSize:16, marginRight:10, verticalAlign:-2}} aria-hidden="true"></i>
            Sign Out
          </div>
        </div>
      </div>
      <div style={styles.main}>{children}</div>
    </div>
  );
}



// ── Dashboard Page ────────────────────────────────────────────────────────────

function DashboardPage({ goToFileManager }) {
  return (
    <div style={styles.page}>
      <Dashboard goToFileManager={goToFileManager} />
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────

function Dashboard({ goToFileManager }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = async () => {
    try {
      const d = await api.get('/dashboard');
      setData(d);
      setLastUpdated(new Date());
    } catch (e) {}
  };

  useEffect(() => {
    load();
    const fast = setInterval(load, 8000);   // refresh every 8s
    return () => clearInterval(fast);
  }, []);

  const fmtBytes = (b) => {
    if (!b) return '0 B';
    if (b < 1024) return `${b} B`;
    if (b < 1048576) return `${(b/1024).toFixed(1)} KB`;
    if (b < 1073741824) return `${(b/1048576).toFixed(1)} MB`;
    return `${(b/1073741824).toFixed(2)} GB`;
  };

  const fmtEta = (secs) => {
    if (!secs) return null;
    if (secs < 60) return `${secs}s`;
    if (secs < 3600) return `${Math.floor(secs/60)}m ${secs%60}s`;
    return `${Math.floor(secs/3600)}h ${Math.floor((secs%3600)/60)}m`;
  };

  if (!data) return (
    <div style={db.loadWrap}>
      <div style={db.loadDot} /><div style={{...db.loadDot, animationDelay:'0.15s'}} /><div style={{...db.loadDot, animationDelay:'0.3s'}} />
    </div>
  );

  const totalFilesToday = data.mappingStats.reduce((s,m) => s + m.filesToday, 0);
  const totalFilesWeek  = data.mappingStats.reduce((s,m) => s + m.filesWeek, 0);
  const totalBytesToday = data.mappingStats.reduce((s,m) => s + m.bytesToday, 0);
  const totalBytesWeek  = data.mappingStats.reduce((s,m) => s + m.bytesWeek, 0);
  const uncQueue        = data.mappingStats.reduce((s,m) => s + m.uncPending, 0);
  const uncFailed       = data.mappingStats.reduce((s,m) => s + (m.uncFailed || 0), 0);

  return (
    <div style={db.wrap}>
      <div style={db.header}>
        <span style={db.title}>Dashboard</span>
        {lastUpdated && <span style={db.updated}>Updated {lastUpdated.toLocaleTimeString()}</span>}
      </div>

      {/* Summary cards */}
      <div style={db.cardRow}>
        <SummaryCard icon="📤" label="Files Today" value={totalFilesToday} sub={`${totalFilesWeek} this week`} color="#3498db"
          onClick={() => goToFileManager(null, null, 'today')} />
        <SummaryCard icon="💾" label="Data Today"  value={fmtBytes(totalBytesToday)} sub={`${fmtBytes(totalBytesWeek)} this week`} color="#9b59b6" />
        <SummaryCard icon="⏫" label="UNC Queue"   value={uncQueue} sub="pending export" color={uncQueue > 0 ? '#e67e22' : '#27ae60'}
          onClick={uncQueue > 0 ? () => goToFileManager(null, null, 'unc-errors') : null} />
        {uncFailed > 0 && <SummaryCard icon="🚫" label="UNC Failed" value={uncFailed} sub="export errors" color="#e74c3c"
          onClick={() => goToFileManager(null, null, 'unc-errors')} />}
        <SummaryCard icon="⚠️" label="Failures"    value={data.recentFailures.length} sub="recent errors" color={data.recentFailures.length > 0 ? '#e74c3c' : '#27ae60'}
          onClick={data.recentFailures.length > 0 ? () => goToFileManager(null, null, 'discrepancies') : null} />
      </div>

      {/* Active transfers */}
      {data.activeTransfers.length > 0 && (
        <div style={db.section}>
          <div style={db.sectionTitle}>🔄 Active Transfers ({data.activeTransfers.length})</div>
          {data.activeTransfers.map(t => (
            <div key={t.sessionId} style={db.transferCard}>
              <div style={db.transferTop}>
                <span style={db.transferName}>{t.clientName} / {t.mappingLabel}</span>
                <span style={db.transferFile}>{t.relativePath}</span>
                <span style={db.transferMeta}>{fmtBytes(t.bytesReceived)} / {fmtBytes(t.totalSizeBytes)}</span>
                {t.etaSeconds != null && <span style={db.transferEta}>ETA {fmtEta(t.etaSeconds)}</span>}
              </div>
              <div style={db.progressTrack}>
                <div style={{...db.progressFill, width: `${t.progressPct}%`}} />
              </div>
              <div style={db.progressLabel}>{t.progressPct}%{t.isBatch ? ' · batch' : ''}</div>
            </div>
          ))}
        </div>
      )}

      {/* Per-mapping stats */}
      <div style={db.section}>
        <div style={db.sectionTitle}>📊 Mapping Status</div>
        <table style={db.table}>
          <thead>
            <tr style={db.thead}>
              <th style={db.th}>Client</th>
              <th style={db.th}>Mapping</th>
              <th style={{...db.th, textAlign:'center'}}>Direction</th>
              <th style={{...db.th, textAlign:'center'}}>Status</th>
              <th style={{...db.th, textAlign:'center'}}>Version</th>
              <th style={{...db.th, textAlign:'right'}}>Files Today</th>
              <th style={{...db.th, textAlign:'right'}}>Data Today</th>
              <th style={{...db.th, textAlign:'right'}}>Files / Week</th>
              <th style={{...db.th, textAlign:'right'}}>Files on server</th>
              <th style={{...db.th, textAlign:'center'}}>Exported (week)</th>
              <th style={{...db.th, textAlign:'right'}}>Export backlog</th>
              <th style={{...db.th, textAlign:'right'}}>Upload backlog</th>
              <th style={{...db.th, textAlign:'right'}}>Client files</th>
            </tr>
          </thead>
          <tbody>
            {data.mappingStats.map(m => (
              <tr key={`${m.clientId}_${m.mappingId}`} style={{...db.tr, cursor:'pointer'}}
                  onClick={() => goToFileManager(m.clientId, m.mappingId, 'discrepancies')}
                  title="Click to view files">
                <td style={db.td}>{m.clientName}</td>
                <td style={db.td}>{m.mappingLabel}</td>
                <td style={{...db.td, textAlign:'center'}}>
                  {m.syncDirection === 'DownloadOnly'
                    ? <span style={{fontSize:11, fontWeight:600, color:'#185FA5', background:'#e6f1fb', padding:'2px 8px', borderRadius:4}}>⬇ Download</span>
                    : <span style={{fontSize:11, fontWeight:600, color:'#1e8449', background:'#eafaf1', padding:'2px 8px', borderRadius:4}}>⬆ Upload</span>}
                </td>
                <td style={{...db.td, textAlign:'center'}}>
                  <HeartDot online={m.isOnline} secondsAgo={m.secondsSinceLastSeen} error={m.clientError} />
                </td>
                <td style={{...db.td, textAlign:'center'}}>
                  {m.serviceVersion
                    ? <span title={`Svc: ${m.serviceVersion}${m.trayVersion ? ` | Tray: ${m.trayVersion}` : ''}`}
                            style={{fontSize:11, fontFamily:'monospace', color:'#555',
                                    background:'#f0f4f8', borderRadius:3, padding:'1px 6px'}}>
                        {m.serviceVersion}
                      </span>
                    : <span style={{color:'#ccc'}}>—</span>}
                </td>
                <td style={{...db.td, textAlign:'right'}}>{m.filesToday}</td>
                <td style={{...db.td, textAlign:'right'}}>{fmtBytes(m.bytesToday)}</td>
                <td style={{...db.td, textAlign:'right'}}>{m.filesWeek}</td>
                <td style={{...db.td, textAlign:'right'}}>{m.diskCount}</td>
                <td style={{...db.td, textAlign:'center'}}>
                  {!m.uncExportEnabled || !m.filesWeek
                    ? <span style={{color:'#bdc3c7'}}>—</span>
                    : (() => {
                        const gap = m.filesWeek - m.weeklyExportedCount;
                        const color = gap === 0 ? '#27ae60'
                          : (gap <= 5 || gap / m.filesWeek <= 0.01) ? '#e67e22' : '#e74c3c';
                        return <span style={{color}}>{m.weeklyExportedCount} / {m.filesWeek}</span>;
                      })()}
                </td>
                <td style={{...db.td, textAlign:'right'}}>
                  {!m.uncExportEnabled ? <span style={{color:'#bdc3c7'}}>—</span>
                    : <span style={{color: m.exportBacklog === 0 ? '#27ae60' : m.exportBacklog <= 25 ? '#e67e22' : '#e74c3c'}}>
                        {m.exportBacklog}
                      </span>}
                </td>
                <td style={{...db.td, textAlign:'right'}}>
                  {m.uploadBacklog == null
                    ? <span style={{color:'#bdc3c7'}}>—</span>
                    : <span style={{color: m.uploadBacklog === 0 ? '#27ae60' : '#e67e22'}}>{m.uploadBacklog}</span>}
                </td>
                <td style={{...db.td, textAlign:'right'}}>
                  {m.clientFileCount == null
                    ? <span style={{color:'#bdc3c7'}}>—</span>
                    : <span style={{color:
                        m.clientFileCount < 50000 ? '#27ae60'
                        : m.clientFileCount < 200000 ? '#e6b800'
                        : m.clientFileCount < 500000 ? '#e67e22' : '#e74c3c'}}>
                        {m.clientFileCount.toLocaleString()}
                      </span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Recent failures */}
      {data.recentFailures.length > 0 && (
        <div style={db.section}>
          <div style={db.sectionTitle}>❌ Recent Failures</div>
          {data.recentFailures.map((f, i) => (
            <div key={i} style={db.failCard}>
              <div style={db.failTop}>
                <span style={db.failClient}>{f.clientName} / {f.mappingLabel}</span>
                <span style={db.failTime}>{new Date(f.failedAtUtc).toLocaleString()}</span>
              </div>
              <div style={db.failFile}>{f.fileName}</div>
              <div style={db.failError}>{f.error}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ icon, label, value, sub, color, onClick }) {
  return (
    <div style={{...db.card, borderTop: `3px solid ${color}`,
                 cursor: onClick ? 'pointer' : 'default',
                 transition: 'transform 0.1s, box-shadow 0.1s'}}
         onClick={onClick}
         onMouseEnter={e => { if (onClick) { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.13)'; }}}
         onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=''; }}>
      <div style={db.cardIcon}>{icon}</div>
      <div style={{...db.cardValue, color}}>{value}</div>
      <div style={db.cardLabel}>{label}</div>
      <div style={db.cardSub}>{sub}</div>
      {onClick && <div style={{fontSize:10, color:'#aaa', marginTop:4}}>click to view →</div>}
    </div>
  );
}

function HeartDot({ online, secondsAgo, error }) {
  const color = online && error ? '#e67e22'  // online but has error — orange
    : online ? '#27ae60'
    : secondsAgo == null ? '#9ca3af'
    : '#e74c3c';
  const label = online && error ? `⚠ Error`
    : online ? 'Online'
    : secondsAgo == null ? 'Never seen'
    : secondsAgo < 3600 ? `${Math.floor(secondsAgo/60)}m ago`
    : secondsAgo < 86400 ? `${Math.floor(secondsAgo/3600)}h ago`
    : `${Math.floor(secondsAgo/86400)}d ago`;
  const tooltip = error ? `Error: ${error}` : label;
  return (
    <span title={tooltip || label} style={{
      display:'inline-flex', alignItems:'center', gap:5,
      fontSize:12, color, fontWeight:600
    }}>
      <span style={{
        width:10, height:10, borderRadius:'50%', background:color,
        boxShadow: online ? `0 0 0 3px ${color}22` : 'none',
        display:'inline-block'
      }}/>
      {label}
    </span>
  );
}

// Dashboard styles
const db = {
  wrap: { padding: '0 0 32px 0' },
  header: { display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:20 },
  title: { fontSize:22, fontWeight:700, color:'#2c3e50' },
  updated: { fontSize:11, color:'#aaa' },
  cardRow: { display:'flex', gap:14, flexWrap:'wrap', marginBottom:24 },
  card: { background:'#fff', borderRadius:10, padding:'16px 20px', minWidth:150, flex:1,
          boxShadow:'0 2px 8px rgba(0,0,0,0.07)' },
  cardIcon: { fontSize:22, marginBottom:6 },
  cardValue: { fontSize:26, fontWeight:800, lineHeight:1 },
  cardLabel: { fontSize:12, fontWeight:600, color:'#555', marginTop:4, textTransform:'uppercase', letterSpacing:.5 },
  cardSub: { fontSize:11, color:'#aaa', marginTop:2 },
  section: { background:'#fff', borderRadius:10, padding:'16px 20px', marginBottom:18,
             boxShadow:'0 2px 8px rgba(0,0,0,0.07)' },
  sectionTitle: { fontSize:13, fontWeight:700, textTransform:'uppercase', letterSpacing:.8,
                  color:'#7f8c8d', marginBottom:14, borderBottom:'1px solid #f0f0f0', paddingBottom:8 },
  transferCard: { background:'#f7f9fc', borderRadius:8, padding:'10px 14px', marginBottom:8 },
  transferTop: { display:'flex', alignItems:'center', gap:12, marginBottom:6, flexWrap:'wrap' },
  transferName: { fontWeight:700, fontSize:13, color:'#2c3e50' },
  transferFile: { fontSize:12, color:'#555', fontFamily:'monospace', flex:1, overflow:'hidden',
                  textOverflow:'ellipsis', whiteSpace:'nowrap' },
  transferMeta: { fontSize:12, color:'#7f8c8d' },
  transferEta: { fontSize:12, color:'#3498db', fontWeight:600 },
  progressTrack: { height:6, background:'#e0e6ed', borderRadius:3, overflow:'hidden' },
  progressFill: { height:'100%', background:'linear-gradient(90deg,#3498db,#2ecc71)',
                  borderRadius:3, transition:'width 0.5s ease' },
  progressLabel: { fontSize:11, color:'#aaa', marginTop:3 },
  table: { width:'100%', borderCollapse:'collapse', fontSize:13 },
  thead: { background:'#f7f9fc' },
  th: { padding:'8px 12px', textAlign:'left', fontWeight:600, fontSize:11,
        textTransform:'uppercase', letterSpacing:.5, color:'#7f8c8d',
        borderBottom:'2px solid #e0e6ed' },
  tr: { borderBottom:'1px solid #f5f5f5' },
  td: { padding:'9px 12px', color:'#2c3e50' },
  failCard: { background:'#fff5f5', border:'1px solid #fdd', borderRadius:8,
              padding:'10px 14px', marginBottom:8 },
  failTop: { display:'flex', justifyContent:'space-between', marginBottom:4 },
  failClient: { fontWeight:700, fontSize:13, color:'#c0392b' },
  failTime: { fontSize:11, color:'#aaa' },
  failFile: { fontSize:12, fontFamily:'monospace', color:'#555', marginBottom:4 },
  failError: { fontSize:12, color:'#e74c3c' },
  loadWrap: { display:'flex', gap:6, padding:24, justifyContent:'center' },
  loadDot: { width:8, height:8, borderRadius:'50%', background:'#3498db',
             animation:'pulse 0.8s ease-in-out infinite' },
};

// ── Clients Page ──────────────────────────────────────────────────────────────

function ClientsPage({ goToFileManager }) {
  const api = useApi();
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [newClientName, setNewClientName] = useState('');
  const [newKey, setNewKey] = useState(null); // {apiKey, aesKey, clientId, mappingId}
  const [loading, setLoading] = useState(true);
  const [showMappingForm, setShowMappingForm] = useState(false);

  const load = async () => {
    setLoading(true);
    const data = await api.get('/clients');
    setClients(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const createClient = async () => {
    if (!newClientName.trim()) return;
    const res = await api.post('/clients', { name: newClientName });
    setNewKey({ type: 'client', apiKey: res.apiKey, clientId: res.id });
    setNewClientName('');
    await load();
    // Auto-select the newly created client
    setSelected({ id: res.id, name: newClientName, mappings: [], mappingCount: 0, enabled: true });
    return res;
  };

  const createClientAndSave = async (mappingData) => {
    if (!newClientName.trim()) return;
    // Create client first
    const clientRes = await api.post('/clients', { name: newClientName });
    setNewClientName('');
    // Then create the mapping under it
    const mapRes = await api.post(`/clients/${clientRes.id}/mappings`, mappingData);
    if (mapRes?.aesKeyBase64) {
      setNewKey({
        type: 'both',
        apiKey: clientRes.apiKey,
        clientId: clientRes.id,
        aesKey: mapRes.aesKeyBase64,
        mappingId: mapRes.id
      });
    } else {
      setNewKey({ type: 'client', apiKey: clientRes.apiKey, clientId: clientRes.id });
    }
    await load();
    setSelected({ id: clientRes.id, name: clientRes.name, mappings: [], mappingCount: 1, enabled: true });
    setShowMappingForm(false);
  };

  const toggleClient = async (c) => {
    await api.patch(`/clients/${c.id}/enabled`, !c.enabled);
    load();
  };

  const deleteClient = async (c) => {
    if (!window.confirm(`Delete client "${c.name}"? This cannot be undone.`)) return;
    await api.del(`/clients/${c.id}`);
    setSelected(null);
    load();
  };

  return (
    <div style={styles.page}>
      <h1 style={styles.pageTitle}>Clients</h1>

      {/* New Key Modal */}
      {newKey && (
        <div style={styles.modal}>
          <div style={styles.modalCard}>
            <h3>⚠️ Save These Credentials Now</h3>
            <p style={{ color: '#e74c3c' }}>These values are shown <strong>once only</strong> and cannot be recovered.</p>
            {newKey.apiKey && <KeyDisplay label="API Key" value={newKey.apiKey} />}
            {newKey.aesKey && <KeyDisplay label="AES-256 Key (Base64)" value={newKey.aesKey} />}
            {newKey.clientId && <KeyDisplay label="Client ID" value={newKey.clientId} />}
            {newKey.mappingId && <KeyDisplay label="Mapping ID" value={newKey.mappingId} />}
            <button style={styles.btn} onClick={() => setNewKey(null)}>I have saved these — Close</button>
          </div>
        </div>
      )}

      <div style={styles.twoCol}>
        {/* Left: client list */}
        <div style={styles.listPanel}>
          <div style={styles.addRow}>
            <input style={{ ...styles.input, marginBottom: 0, flex: 1 }}
                   placeholder="New client name" value={newClientName}
                   onChange={e => setNewClientName(e.target.value)}
                   onKeyDown={e => e.key === 'Enter' && createClient()} />
            <button style={{ ...styles.btn, marginLeft: 8, padding: '8px 14px' }}
                    onClick={createClient}>Add</button>
          </div>
          {loading ? <Spinner /> : clients.map(c => (
            <div key={c.id}
                 style={{ ...styles.listItem, ...(selected?.id === c.id ? styles.listItemActive : {}),
                          gap:10, padding:'10px 12px' }}
                 onClick={() => setSelected(c)}>
              <div style={{ width:34, height:34, borderRadius:8, background: selected?.id === c.id ? '#bfdbfe' : '#e2e8f0',
                            display:'flex', alignItems:'center', justifyContent:'center',
                            fontSize:12, fontWeight:500, color: selected?.id === c.id ? '#1d4ed8' : '#4a5568', flexShrink:0 }}>
                {c.name.slice(0,2).toUpperCase()}
              </div>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontSize:13, fontWeight:500, color:'#1a202c', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{c.name}</div>
                <div style={{fontSize:11, color:'#718096'}}>{c.mappingCount} mapping{c.mappingCount !== 1 ? 's' : ''}</div>
              </div>
              <span style={{ width:8, height:8, borderRadius:'50%', background: c.enabled ? '#48bb78' : '#fc8181', flexShrink:0 }}></span>
            </div>
          ))}
        </div>

        {/* Right: client detail */}
        {selected && (
          <ClientDetail
            client={selected}
            api={api}
            onRefresh={() => { load(); setSelected(s => ({ ...s })); }}
            onToggle={() => toggleClient(selected)}
            onDelete={() => deleteClient(selected)}
            onNewKey={setNewKey}
            goToFileManager={goToFileManager}
          />
        )}
      </div>
    </div>
  );
}

function ClientDetail({ client, api, onRefresh, onToggle, onDelete, onNewKey, goToFileManager }) {
  const [mappings, setMappings] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editMapping, setEditMapping] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState('');

  const doRename = async () => {
    if (!newName.trim()) return;
    await api.patch(`/clients/${client.id}/name`, { name: newName.trim() });
    setRenaming(false);
    onRefresh();
  };

  const loadMappings = async () => {
    const data = await api.get(`/clients/${client.id}`);
    setMappings(data?.mappings || []);
  };

  useEffect(() => { loadMappings(); }, [client.id]);

  const deleteMapping = async (m) => {
    if (!window.confirm(`Delete mapping "${m.label}"?`)) return;
    await api.del(`/clients/${client.id}/mappings/${m.id}`);
    loadMappings();
  };

  return (
    <div style={styles.detailPanel}>
      <div style={styles.detailHeader}>
        {renaming ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input style={{ ...styles.input, marginBottom: 0, width: 220 }}
                   value={newName} onChange={e => setNewName(e.target.value)}
                   onKeyDown={e => { if (e.key === 'Enter') doRename(); if (e.key === 'Escape') setRenaming(false); }}
                   autoFocus />
            <button style={styles.btnSm} onClick={doRename}>Save</button>
            <button style={{ ...styles.btnSm, background: '#7f8c8d' }} onClick={() => setRenaming(false)}>Cancel</button>
          </div>
        ) : (
          <h2 style={{ margin:0, fontSize:18, fontWeight:500, cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}
              title="Click to rename" onClick={() => { setNewName(client.name); setRenaming(true); }}>
            {client.name}
            <i className="ti ti-pencil" style={{fontSize:14, color:'#a0aec0'}} aria-hidden="true"></i>
          </h2>
        )}
        <div>
          <button style={{ ...styles.btnSm, background:'#f8fafc', color:'#374151', border:'0.5px solid #e2e8f0', marginRight:6 }}
                  onClick={onToggle}>
            {client.enabled ? 'Disable' : 'Enable'}
          </button>
          <button style={{ ...styles.btnSm, background:'#fef2f2', color:'#991b1b', border:'0.5px solid #fca5a5' }}
                  onClick={onDelete}>Delete</button>
        </div>
      </div>
      <div style={styles.small}>ID: {client.id} · Created: {new Date(client.createdUtc).toLocaleDateString()}</div>

      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:20, marginBottom:12}}>
        <div style={{fontSize:13, fontWeight:500, color:'#4a5568', textTransform:'uppercase', letterSpacing:'0.05em'}}>Watch Folder Mappings</div>
        {!showForm && (
          <button style={{ ...styles.btnSm, background:'#f0fdf4', color:'#166534', border:'0.5px solid #86efac' }}
                  onClick={() => { setEditMapping(null); setShowForm(true); }}>
            + Add Mapping
          </button>
        )}
      </div>
      {mappings.map(m => (
        <div key={m.id} style={styles.mappingCard}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:14, fontWeight:500, color:'#1a202c' }}>{m.label}</span>
              {m.syncDirection === 'DownloadOnly'
                ? <span style={{fontSize:11,fontWeight:500,color:'#1d4ed8',background:'#dbeafe',padding:'2px 8px',borderRadius:99}}>⬇ Download</span>
                : <span style={{fontSize:11,fontWeight:500,color:'#166534',background:'#dcfce7',padding:'2px 8px',borderRadius:99}}>⬆ Upload</span>}
              {m.uncExportEnabled && <span style={{fontSize:11,color:'#92400e',background:'#fef3c7',padding:'2px 8px',borderRadius:99}}>UNC</span>}
              {m.batchingEnabled && <span style={{fontSize:11,color:'#5b21b6',background:'#ede9fe',padding:'2px 8px',borderRadius:99}}>Batch</span>}
            </div>
            <div style={{ display:'flex', gap:6 }}>
              <button style={{ ...styles.btnSm, background:'#f0fdf4', color:'#166534', border:'0.5px solid #86efac' }}
                      onClick={() => goToFileManager(client.id, m.id, 'discrepancies')}>
                <i className="ti ti-folder" style={{fontSize:12,marginRight:4}} aria-hidden="true"></i>View Files
              </button>
              <button style={{ ...styles.btnSm, background:'#f8fafc', color:'#374151', border:'0.5px solid #e2e8f0' }}
                      onClick={() => { setEditMapping(m); setShowForm(true); }}>Edit</button>
              <button style={{ ...styles.btnSm, background:'#fef2f2', color:'#991b1b', border:'0.5px solid #fca5a5' }}
                      onClick={() => deleteMapping(m)}>Remove</button>
            </div>
          </div>
          <div style={{fontSize:12, color:'#718096', display:'flex', gap:16, flexWrap:'wrap'}}>
            <span><i className="ti ti-folder-open" style={{fontSize:12,marginRight:3,verticalAlign:-1}} aria-hidden="true"></i>{m.serverFolderPath}</span>
            <span>Reconcile {m.reconciliationIntervalMinutes}min</span>
            <span>Chunks {m.chunkSizeMb}MB</span>
            <span>{m.parallelWorkers} workers</span>
            {m.uncExportEnabled && <span style={{color:'#92400e'}}>{m.uncPath}</span>}
          </div>
          {m.uncExportEnabled && (
            <div style={{ marginTop: 8 }}>
              <button style={{ ...styles.btnSm, background: '#f39c12' }}
                      onClick={async () => {
                        if (!window.confirm('Mark all existing files for UNC export? This will queue them for delivery to the UNC path.')) return;
                        await api.post(`/clients/${client.id}/mappings/${m.id}/manifest/enable-unc`);
                        alert('Done — files will export within 5 minutes.');
                      }}>
                ↑ Apply UNC export to existing files
              </button>
            </div>
          )}
        </div>
      ))}

      {showForm && (
        <MappingForm
          initial={editMapping ? {...editMapping,
            uncUsernameSet: !!editMapping.uncUsernameEncrypted || editMapping.useDefaultUncCredentials,
            uncPasswordSet: !!editMapping.uncPasswordEncrypted || editMapping.useDefaultUncCredentials
          } : null}
          clientId={client.id}
          mappingId={editMapping?.id}
          api={api}
          onSave={async (data) => {
            let res;
            if (editMapping) {
              await api.put(`/clients/${client.id}/mappings/${editMapping.id}`, data);
            } else {
              res = await api.post(`/clients/${client.id}/mappings`, data);
              if (res?.aesKeyBase64) {
                onNewKey({ type: 'mapping', aesKey: res.aesKeyBase64, mappingId: res.id, clientId: client.id });
              }
            }
            setShowForm(false);
            setEditMapping(null);
            loadMappings();
          }}
          onCancel={() => { setShowForm(false); setEditMapping(null); }}
        />
      )}
    </div>
  );
}

function MappingForm({ initial, onSave, onCancel, clientId, mappingId, api }) {
  const [f, setF] = useState({
    label: initial?.label || '',
    serverFolderPath: initial?.serverFolderPath || '',
    reconciliationIntervalMinutes: initial?.reconciliationIntervalMinutes || 5,
    chunkSizeMb: initial?.chunkSizeMb || 5,
    parallelWorkers: initial?.parallelWorkers || 4,
    batchingEnabled: initial?.batchingEnabled || false,
    batchSmallFileThresholdMb: initial?.batchSmallFileThresholdMb || 2,
    batchMaxSizeMb: initial?.batchMaxSizeMb || 20,
    batchMaxWaitSeconds: initial?.batchMaxWaitSeconds || 5,
    uncExportEnabled: initial?.uncExportEnabled || false,
    uncPath: initial?.uncPath || '',
    uncUsername: '',
    uncPassword: '',
    useDefaultUncCredentials: initial?.useDefaultUncCredentials || false,
    syncDirection: initial?.syncDirection || 'UploadOnly',
    useFullUncPath: initial?.useFullUncPath || false,
    exclusionPatterns: initial?.exclusionPatterns || [],
  });

  const [uncTesting, setUncTesting] = useState(false);
  const [uncTestResult, setUncTestResult] = useState(null);
  const [globalUncBase, setGlobalUncBase] = useState('');

  useEffect(() => {
    if (api) api.get('/config').then(cfg => setGlobalUncBase(cfg?.globalUncBasePath || ''));
  }, []);

  const testUnc = async () => {
    setUncTesting(true);
    setUncTestResult(null);
    try {
      let result;
      // Build the full UNC path for testing
      const base = (globalUncBase || '').replace(/[\\]+$/, '');
      const sub = (f.uncPath || '').replace(/^[\\]+/, '');
      const fullPath = f.useFullUncPath || !globalUncBase
        ? f.uncPath
        : base + '\\' + sub;
      console.log('UNC test path:', fullPath, 'base:', base, 'sub:', sub);

      if (f.useDefaultUncCredentials && initial && mappingId) {
        // Saved mapping with global creds — server resolves creds from saved config
        result = await api.post(`/clients/${clientId}/mappings/${mappingId}/test-unc`);
      } else if (f.useDefaultUncCredentials) {
        // New/unsaved mapping with global creds — test using saved global creds + provided path
        result = await api.post('/config/nas-credentials/test-with-defaults', { uncPath: fullPath });
      } else {
        // Use credentials from the form
        result = await api.post('/config/nas-credentials/test', {
          uncPath: fullPath,
          username: f.uncUsername,
          password: f.uncPassword
        });
      }
      setUncTestResult(result);
    } catch (e) { setUncTestResult({ success: false, message: e?.message || String(e) }); }
    finally { setUncTesting(false); }
  };

  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  return (
    <div style={styles.formCard}>
      <h3>{initial ? 'Edit Mapping' : 'New Mapping'}</h3>

      <Section title="General">
        <Field label="Label"><input style={styles.input} value={f.label} onChange={e => set('label', e.target.value)} /></Field>
        <Field label="Sync Direction">
          <select style={{ ...styles.input, marginBottom: 0 }}
                  value={f.syncDirection || 'UploadOnly'}
                  onChange={e => set('syncDirection', e.target.value)}>
            <option value="UploadOnly">Upload Only — client sends files to server</option>
            <option value="DownloadOnly">Download Only — server receives from UNC, client downloads</option>
          </select>
        </Field>
        <Field label="Server Folder Name">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: '#7f8c8d', fontSize: 13, whiteSpace: 'nowrap' }}>/data/clients/</span>
            <input style={{ ...styles.input, marginBottom: 0, flex: 1 }}
                   value={f.serverFolderPath.replace(/^\/data\/clients\//, '')}
                   onChange={e => set('serverFolderPath', e.target.value)}
                   placeholder="clientname/sitename" />
          </div>
        </Field>
      </Section>

      <Section title="Sync Settings">
        <Field label="Reconcile Interval (min)"><NumInput value={f.reconciliationIntervalMinutes} min={1} max={1440} onChange={v => set('reconciliationIntervalMinutes', v)} /></Field>
        <Field label="Chunk Size (MB)"><NumInput value={f.chunkSizeMb} min={1} max={100} onChange={v => set('chunkSizeMb', v)} /></Field>
        <Field label="Parallel Workers"><NumInput value={f.parallelWorkers} min={1} max={16} onChange={v => set('parallelWorkers', v)} /></Field>
      </Section>

      <Section title="Batching">
        <Field label="Enable Batching">
          <input type="checkbox" checked={f.batchingEnabled} onChange={e => set('batchingEnabled', e.target.checked)} />
        </Field>
        <Field label="Small File Threshold (MB)" dim={!f.batchingEnabled}>
          <NumInput value={f.batchSmallFileThresholdMb} min={1} max={50} onChange={v => set('batchSmallFileThresholdMb', v)} disabled={!f.batchingEnabled} />
        </Field>
        <Field label="Batch Max Size (MB)" dim={!f.batchingEnabled}>
          <NumInput value={f.batchMaxSizeMb} min={1} max={200} onChange={v => set('batchMaxSizeMb', v)} disabled={!f.batchingEnabled} />
        </Field>
        <Field label="Batch Max Wait (sec)" dim={!f.batchingEnabled}>
          <NumInput value={f.batchMaxWaitSeconds} min={1} max={300} onChange={v => set('batchMaxWaitSeconds', v)} disabled={!f.batchingEnabled} />
        </Field>
      </Section>

      <Section title="File Exclusions">
        <p style={{ fontSize: 13, color: '#7f8c8d', marginTop: 0 }}>
          Per-mapping exclusion patterns. One per line. Supports wildcards: *.tmp, .sync/**, ~$*
        </p>
        <textarea style={{ ...styles.input, height: 80, fontFamily: 'monospace', fontSize: 12 }}
                  value={(f.exclusionPatterns || []).join('\n')}
                  onChange={e => set('exclusionPatterns', e.target.value.split('\n'))}
                  placeholder=".sync/**&#10;*.tmp" />
      </Section>
      <Section title="UNC Export">
        <Field label="Enable UNC Export">
          <input type="checkbox" checked={f.uncExportEnabled} onChange={e => set('uncExportEnabled', e.target.checked)} />
        </Field>
        <Field label="UNC Path Mode" dim={!f.uncExportEnabled}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={f.useFullUncPath || false}
                   onChange={e => set('useFullUncPath', e.target.checked)}
                   disabled={!f.uncExportEnabled} />
            Enter full UNC path (override global base path)
          </label>
        </Field>
        {f.useFullUncPath ? (
          <Field label="Full UNC Path" dim={!f.uncExportEnabled}>
            <input style={styles.input} value={f.uncPath} onChange={e => set('uncPath', e.target.value)}
                   placeholder="\\\\server\\share\\folder"
                   disabled={!f.uncExportEnabled} />
          </Field>
        ) : (
          <Field label="UNC Subfolder" dim={!f.uncExportEnabled}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: '#7f8c8d', fontSize: 13, whiteSpace: 'nowrap', maxWidth: 200,
                             overflow: 'hidden', textOverflow: 'ellipsis' }}
                    title={globalUncBase || 'No global base path set'}>
                {globalUncBase ? `${globalUncBase}\\` : <span style={{color:'#e74c3c'}}>No global base set</span>}
              </span>
              <input style={{ ...styles.input, marginBottom: 0, flex: 1 }}
                     value={f.uncPath} onChange={e => set('uncPath', e.target.value)}
                     placeholder="clientname\\sitename"
                     disabled={!f.uncExportEnabled} />
            </div>
          </Field>
        )}
        <Field label="Use Default Credentials" dim={!f.uncExportEnabled}>
          <input type="checkbox" checked={f.useDefaultUncCredentials} onChange={e => set('useDefaultUncCredentials', e.target.checked)} disabled={!f.uncExportEnabled} />
          <span style={{ fontSize: 12, color: '#7f8c8d', marginLeft: 8 }}>Use global NAS credentials from Settings</span>
        </Field>
        <Field label="UNC Username" dim={!f.uncExportEnabled || f.useDefaultUncCredentials}>
          <input style={styles.input} value={f.uncUsername} onChange={e => set('uncUsername', e.target.value)}
                 disabled={!f.uncExportEnabled || f.useDefaultUncCredentials}
                 placeholder={f.useDefaultUncCredentials ? '(using default)' : initial?.uncUsernameSet ? '(saved — leave blank to keep)' : ''} />
        </Field>
        <Field label="UNC Password" dim={!f.uncExportEnabled || f.useDefaultUncCredentials}>
          <input style={styles.input} type="password" value={f.uncPassword} onChange={e => set('uncPassword', e.target.value)}
                 disabled={!f.uncExportEnabled || f.useDefaultUncCredentials}
                 placeholder={f.useDefaultUncCredentials ? '(using default)' : initial?.uncPasswordSet ? '(saved — leave blank to keep)' : ''} />
        </Field>
        {f.uncExportEnabled && uncTestResult !== null && (
          <div style={{ marginLeft: 168, padding: '6px 12px', borderRadius: 6, fontSize: 13,
                        background: uncTestResult.success ? '#eafaf1' : '#fef5ec',
                        color: uncTestResult.success ? '#27ae60' : '#e74c3c' }}>
            {uncTestResult.success ? '✓' : '✗'} {uncTestResult.message}
          </div>
        )}
        {f.uncExportEnabled && (
          <div style={{ marginLeft: 168, marginTop: 4 }}>
            <button style={{ ...styles.btnSm, background: '#7f8c8d' }}
                    onClick={testUnc} disabled={uncTesting}>
              {uncTesting ? 'Testing...' : '🔌 Test UNC Connection'}
            </button>
          </div>
        )}
      </Section>

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button style={styles.btn} onClick={() => onSave(f)}>Save Mapping</button>
        <button style={{ ...styles.btn, background: '#7f8c8d' }} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── Settings Page ─────────────────────────────────────────────────────────────

function SettingsPage() {
  const api = useApi();
  const [cfg, setCfg] = useState(null);
  const [saved, setSaved] = useState(false);
  const [nasUser, setNasUser] = useState('');
  const [nasPass, setNasPass] = useState('');
  const [nasTestPath, setNasTestPath] = useState('');
  const [nasTestResult, setNasTestResult] = useState(null);
  const [nasTesting, setNasTesting] = useState(false);
  const [nasSaving, setNasSaving] = useState(false);
  const [ldapInput, setLdapInput] = useState('');

  useEffect(() => { api.get('/config').then(d => { setCfg(d); setLdapInput((d?.allowedLdapUsers || []).join('\n')); }); }, []);

  const set = (k, v) => setCfg(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    await api.put('/config', cfg);
    const updated = await api.get('/config');
    setCfg(updated);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const saveNasCreds = async () => {
    setNasSaving(true);
    await api.post('/config/nas-credentials', { uncPath: '', username: nasUser, password: nasPass });
    setNasUser(''); setNasPass('');
    const updated = await api.get('/config');
    setCfg(updated);
    setNasSaving(false);
  };

  const clearNasCreds = async () => {
    if (!window.confirm('Clear global NAS credentials?')) return;
    await api.del('/config/nas-credentials');
    const updated = await api.get('/config');
    setCfg(updated);
  };

  const testNas = async () => {
    setNasTesting(true); setNasTestResult(null);
    const result = await api.post('/config/nas-credentials/test', { uncPath: nasTestPath, username: nasUser, password: nasPass });
    setNasTestResult(result);
    setNasTesting(false);
  };

  const saveLdapWhitelist = async () => {
    const users = ldapInput.split('\n').map(u => u.trim()).filter(Boolean);
    await api.put('/config/ldap-whitelist', { allowedUsers: users });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  if (!cfg) return <Spinner />;

  return (
    <div style={styles.page}>
      <h1 style={styles.pageTitle}>Global Settings</h1>

      <Section title="Sync Defaults">
        <Field label="Default Reconcile Interval (min)"><NumInput value={cfg.defaultReconciliationIntervalMinutes} min={1} max={1440} onChange={v => set('defaultReconciliationIntervalMinutes', v)} /></Field>
        <Field label="Default Chunk Size (MB)"><NumInput value={cfg.defaultChunkSizeMb} min={1} max={100} onChange={v => set('defaultChunkSizeMb', v)} /></Field>
        <Field label="Default Parallel Workers"><NumInput value={cfg.defaultParallelWorkers} min={1} max={16} onChange={v => set('defaultParallelWorkers', v)} /></Field>
      </Section>

      <Section title="Log Retention">
        <Field label="Retain Logs (days)"><NumInput value={cfg.logRetentionDays} min={7} max={3650} onChange={v => set('logRetentionDays', v)} /></Field>
      </Section>

      <Section title="Global UNC Base Path">
        <p style={{ fontSize: 13, color: '#7f8c8d', marginTop: 0 }}>
          Set once — mappings only need to enter the subfolder.
          {cfg.globalUncBasePath
            ? <strong style={{ color: '#27ae60' }}> Current: {cfg.globalUncBasePath}</strong>
            : <strong style={{ color: '#e67e22' }}> Not set.</strong>}
        </p>
        <Field label="Base UNC Path">
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={{ ...styles.input, marginBottom: 0, flex: 1 }}
                   value={cfg.globalUncBasePath || ''}
                   onChange={e => set('globalUncBasePath', e.target.value)}
                   placeholder="\\\\server\\share" />
            <button style={styles.btn} onClick={async () => {
              await api.put('/config/unc-base-path', { globalUncBasePath: cfg.globalUncBasePath });
              setSaved(true); setTimeout(() => setSaved(false), 3000);
            }}>Save</button>
          </div>
        </Field>
      </Section>

      <Section title="Global NAS Credentials">
        <p style={{ fontSize: 13, color: '#7f8c8d', marginTop: 0 }}>
          Default credentials used by mappings with "Use Default Credentials" enabled.
          {cfg.defaultNasCredentialsSaved
            ? <strong style={{ color: '#27ae60' }}> ✓ Credentials are saved.</strong>
            : <strong style={{ color: '#e67e22' }}> No credentials saved yet.</strong>}
        </p>
        <Field label="NAS Username"><input style={styles.input} value={nasUser} onChange={e => setNasUser(e.target.value)} placeholder="domain\\username or username" /></Field>
        <Field label="NAS Password"><input style={styles.input} type="password" value={nasPass} onChange={e => setNasPass(e.target.value)} /></Field>
        <Field label="Test UNC Path">
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={{ ...styles.input, marginBottom: 0, flex: 1 }} value={nasTestPath}
                   onChange={e => setNasTestPath(e.target.value)} placeholder="\\server\share" />
            <button style={{ ...styles.btnSm, background: '#7f8c8d' }} onClick={testNas} disabled={nasTesting}>
              {nasTesting ? 'Testing...' : 'Test'}
            </button>
          </div>
        </Field>
        {nasTestResult && (
          <div style={{ marginLeft: 220, padding: '6px 12px', borderRadius: 6, fontSize: 13,
                        background: nasTestResult.success ? '#eafaf1' : '#fef5ec',
                        color: nasTestResult.success ? '#27ae60' : '#e74c3c' }}>
            {nasTestResult.success ? '✓' : '✗'} {nasTestResult.message}
          </div>
        )}
        <div style={{ marginLeft: 220, display: 'flex', gap: 8, marginTop: 8 }}>
          <button style={styles.btn} onClick={saveNasCreds} disabled={nasSaving}>
            {nasSaving ? 'Saving...' : 'Save NAS Credentials'}
          </button>
          {cfg.defaultNasCredentialsSaved && (
            <button style={{ ...styles.btn, background: '#e74c3c' }} onClick={clearNasCreds}>Clear</button>
          )}
        </div>
      </Section>

      <Section title="Agent Registration">
        <p style={{ fontSize: 13, color: '#7f8c8d', marginTop: 0 }}>
          URL that pre-configured agent installers use to reach this server.
          Used when pushing config to pending agents.
        </p>
        <Field label="Agent Server URL">
          <input style={styles.input} value={cfg.agentServerUrl || ''}
                 onChange={e => set('agentServerUrl', e.target.value)}
                 placeholder="https://your-server.com" />
        </Field>
      </Section>

      <Section title="Manifest Retention">
        <p style={{ fontSize: 13, color: '#7f8c8d', marginTop: 0 }}>
          Days to keep processed file history (UNC exported uploads and confirmed downloads) before permanent deletion.
          Also controls how long before an unconfirmed download is flagged as overdue.
        </p>
        <Field label="Retention days">
          <NumInput value={cfg.manifestRetentionDays ?? 30} min={1} max={365} onChange={v => set('manifestRetentionDays', v)} />
        </Field>
        <Field label="Download overdue after (minutes)">
          <NumInput value={cfg.downloadOverdueAfterMinutes ?? 120} min={15} max={1440} onChange={v => set('downloadOverdueAfterMinutes', v)} />
        </Field>
      </Section>

      <Section title="Global File Exclusions">
        <p style={{ fontSize: 13, color: '#7f8c8d', marginTop: 0 }}>
          Patterns applied to ALL mappings on ALL clients. One per line.
          Supports: *.ext, prefix/**, exact-name
        </p>
        <textarea style={{ ...styles.input, height: 120, fontFamily: 'monospace', fontSize: 12 }}
                  value={(cfg.globalExclusionPatterns || []).join('\n')}
                  onChange={e => set('globalExclusionPatterns', [...new Set(e.target.value.split('\n').filter(Boolean))])}
                  placeholder=".sync/**&#10;*.tmp&#10;desktop.ini&#10;~$*" />
        <button style={{ ...styles.btn, marginTop: 8 }} onClick={async () => {
          await api.put('/exclusions/global', { globalPatterns: [...new Set(cfg.globalExclusionPatterns || [])] });
          setSaved(true); setTimeout(() => setSaved(false), 3000);
        }}>Save Exclusions</button>
      </Section>

      <Section title="Active Directory / LDAP">
        <Field label="Enable AD Auth"><input type="checkbox" checked={cfg.ldapEnabled} onChange={e => set('ldapEnabled', e.target.checked)} /></Field>
        <Field label="AD Server Host"><input style={styles.input} value={cfg.ldapHost || ''} onChange={e => set('ldapHost', e.target.value)} disabled={!cfg.ldapEnabled} /></Field>
        <Field label="LDAP Port"><NumInput value={cfg.ldapPort || 389} min={1} max={65535} onChange={v => set('ldapPort', v)} disabled={!cfg.ldapEnabled} /></Field>
        <Field label="Use SSL"><input type="checkbox" checked={cfg.ldapUseSsl || false} onChange={e => set('ldapUseSsl', e.target.checked)} disabled={!cfg.ldapEnabled} /></Field>
        <Field label="Base DN"><input style={styles.input} value={cfg.ldapBaseDn || ''} onChange={e => set('ldapBaseDn', e.target.value)} placeholder="DC=corp,DC=example,DC=com" disabled={!cfg.ldapEnabled} /></Field>
      </Section>

      <Section title="AD Login Whitelist">
        <p style={{ fontSize: 13, color: '#7f8c8d', marginTop: 0 }}>
          Only these AD usernames can log in. One per line. Leave empty to allow all AD users.
        </p>
        <textarea style={{ ...styles.input, height: 120, fontFamily: 'monospace', fontSize: 13 }}
                  value={ldapInput} onChange={e => setLdapInput(e.target.value)}
                  placeholder="jsmith&#10;bjones&#10;admin" />
        <button style={{ ...styles.btn, marginTop: 8 }} onClick={saveLdapWhitelist}>Save Whitelist</button>
      </Section>

      <button style={styles.btn} onClick={save}>Save Settings</button>
      {saved && <span style={{ marginLeft: 12, color: '#27ae60' }}>✓ Saved</span>}
    </div>
  );
}

// ── Shared Components ─────────────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={styles.sectionHeader}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, children, dim }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
      <label style={{ ...styles.fieldLabel, opacity: dim ? 0.4 : 1 }}>{label}</label>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

function NumInput({ value, min, max, onChange, disabled }) {
  return (
    <input type="number" style={{ ...styles.input, width: 100, marginBottom: 0 }}
           value={value} min={min} max={max} disabled={disabled}
           onChange={e => onChange(parseInt(e.target.value) || min)} />
  );
}

function KeyDisplay({ label, value }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={styles.small}>{label}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input readOnly style={{ ...styles.input, fontFamily: 'monospace', fontSize: 12, marginBottom: 0, flex: 1 }} value={value} />
        <button style={styles.btnSm} onClick={() => { navigator.clipboard.writeText(value); setCopied(true); }}>
          {copied ? '✓' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

function Spinner() {
  return <div style={{ padding: 20, color: '#7f8c8d' }}>Loading...</div>;
}

// ── Root App ──────────────────────────────────────────────────────────────────

function App() {
  const [page, setPage] = useState('dashboard');
  const [fileManagerFilter, setFileManagerFilter] = useState(null);
  const [fileManagerClientId, setFileManagerClientId] = useState(null);
  const [fileManagerMappingId, setFileManagerMappingId] = useState(null);

  const goToFileManager = (clientId, mappingId, filter) => {
    setFileManagerClientId(clientId || null);
    setFileManagerMappingId(mappingId || null);
    setFileManagerFilter(filter || null);
    setPage('files');
  };
  const { isAuthed } = useAuth();

  if (!isAuthed) return <LoginPage />;

  return (
    <Shell page={page} setPage={setPage}>
      {page === 'dashboard' && <DashboardPage goToFileManager={goToFileManager} />}
      {page === 'clients' && <ClientsPage goToFileManager={goToFileManager} />}
      {page === 'files' && <FileManagerPage
        initialFilter={fileManagerFilter}
        initialClientId={fileManagerClientId}
        initialMappingId={fileManagerMappingId}
        clearFilter={() => { setFileManagerFilter(null); setFileManagerClientId(null); setFileManagerMappingId(null); }} />}
      {page === 'settings' && <SettingsPage />}
      {page === 'users' && <UsersPage />}
      {page === 'agents' && <AgentsPage />}
      {page === 'updates' && <UpdatesPage />}
    </Shell>
  );
}

// ── File Manager Page ─────────────────────────────────────────────────────────

function RedownloadBtn({ clientId, mappingId, filePath, api, onDone }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const doRedownload = async () => {
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post(`/clients/${clientId}/mappings/${mappingId}/reingest`, { filePath });
      setResult(r);
      if (r?.success) onDone();
    } catch (e) { setResult({ success: false, message: e.message }); }
    setLoading(false);
  };

  return (
    <div>
      <button style={{ fontSize: 11, padding: '2px 8px', background: '#eaf4fb',
                       border: '1px solid #aed6f1', borderRadius: 4, cursor: 'pointer',
                       color: '#2980b9', whiteSpace: 'nowrap' }}
              onClick={doRedownload} disabled={loading}>
        {loading ? '...' : '↓ Re-download'}
      </button>
      {result && !result.success && (
        <div style={{ fontSize: 10, color: '#e74c3c', marginTop: 2, maxWidth: 120,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
             title={result.message}>✗ {result.message}</div>
      )}
    </div>
  );
}

function SyncErrorList({ clientId, mappingId, api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.get(`/clients/${clientId}/mappings/${mappingId}/sync-errors`);
      setData(d);
    } catch (e) { setData({ errors: [] }); }
    setLoading(false);
  };

  useEffect(() => { if (clientId && mappingId) load(); }, [clientId, mappingId]);

  if (loading) return <Spinner />;
  if (!data || data.errors.length === 0)
    return <div style={{ padding: 24, color: '#7f8c8d', textAlign: 'center' }}>No sync errors recorded.</div>;

  return (
    <div>
      <div style={{ fontSize: 13, color: '#7f8c8d', fontWeight: 600, marginBottom: 8 }}>
        {data.errors.length} recorded error{data.errors.length !== 1 ? 's' : ''} — current status may already be resolved
      </div>
      {data.errors.map((e, i) => (
        <div key={i} style={{ background: '#fff5f5', border: '1px solid #fdd', borderRadius: 8,
                              padding: '10px 14px', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 11, color: '#aaa', whiteSpace: 'nowrap' }}>{new Date(e.occurredUtc).toLocaleString()}</span>
          </div>
          <div style={{ fontSize: 12, color: '#e74c3c', marginTop: 4, fontFamily: 'monospace' }}>{e.message}</div>
        </div>
      ))}
    </div>
  );
}

function UncErrorList({ clientId, mappingId, api, onAction }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.get(`/clients/${clientId}/mappings/${mappingId}/unc-errors`);
      setData(d);
    } catch (e) { setData({ failures: [], ignoredFiles: [] }); }
    setLoading(false);
  };

  useEffect(() => { if (clientId && mappingId) load(); }, [clientId, mappingId]);

  const doAction = async (fileName, action) => {
    const label = action === 'ignore' ? 'permanently ignore' : 'do a fresh retry';
    if (!window.confirm(`${label === 'permanently ignore' ? 'Permanently ignore' : 'Reset'} "${fileName}"?`)) return;
    await api.post(`/clients/${clientId}/mappings/${mappingId}/unc-errors/action`,
      { filePath: fileName, action });
    load();
    onAction();
  };

  if (loading) return <Spinner />;
  if (!data || (data.failures.length === 0 && data.ignoredFiles.length === 0))
    return <div style={{ padding: 24, color: '#7f8c8d', textAlign: 'center' }}>No UNC export errors.</div>;

  return (
    <div>
      {data.failures.length > 0 && (
        <>
          <div style={{ fontSize: 13, color: '#e74c3c', fontWeight: 600, marginBottom: 8 }}>
            {data.failures.length} file{data.failures.length !== 1 ? 's' : ''} failing UNC export
          </div>
          {data.failures.map((f, i) => (
            <div key={i} style={{ background: '#fff5f5', border: '1px solid #fdd', borderRadius: 8,
                                  padding: '10px 14px', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontFamily: 'monospace', fontSize: 12, flex: 1, color: '#555' }}>{f.fileName}</span>
                <span style={{ fontSize: 11, color: '#aaa' }}>{new Date(f.failedAtUtc).toLocaleString()}</span>
                <button style={{ fontSize: 11, padding: '3px 10px', background: '#e74c3c',
                                 border: 'none', borderRadius: 4, cursor: 'pointer', color: '#fff' }}
                        onClick={() => doAction(f.fileName, 'ignore')}>
                  Ignore Forever
                </button>
                <button style={{ fontSize: 11, padding: '3px 10px', background: '#3498db',
                                 border: 'none', borderRadius: 4, cursor: 'pointer', color: '#fff' }}
                        onClick={() => doAction(f.fileName, 'doover')}>
                  Do Over
                </button>
              </div>
              <div style={{ fontSize: 11, color: '#e74c3c', marginTop: 4 }}>{f.error}</div>
            </div>
          ))}
        </>
      )}
      {data.ignoredFiles.length > 0 && (
        <>
          <div style={{ fontSize: 13, color: '#7f8c8d', fontWeight: 600, marginTop: 16, marginBottom: 8 }}>
            {data.ignoredFiles.length} permanently ignored
          </div>
          {data.ignoredFiles.map((f, i) => (
            <div key={i} style={{ background: '#f8f8f8', borderRadius: 6, padding: '8px 12px',
                                  marginBottom: 6, fontFamily: 'monospace', fontSize: 12, color: '#aaa' }}>
              {f}
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function IgnoredList({ clientId, mappingId, api, onUnignore }) {
  const [ignored, setIgnored] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get(`/clients/${clientId}/mappings/${mappingId}/ignored`);
      setIgnored(data || []);
    } catch (e) { setIgnored([]); }
    setLoading(false);
  };

  useEffect(() => { if (clientId && mappingId) load(); }, [clientId, mappingId]);

  const unignore = async (path) => {
    await api.del(`/clients/${clientId}/mappings/${mappingId}/ignore?filePath=${encodeURIComponent(path)}`);
    load();
    onUnignore();
  };

  if (loading) return <Spinner />;
  if (ignored.length === 0) return (
    <div style={{ padding: 24, color: '#7f8c8d', textAlign: 'center' }}>
      <div style={{ fontSize: 16, marginBottom: 8 }}>No ignored discrepancies</div>
      <div style={{ fontSize: 12, maxWidth: 400, margin: '0 auto', lineHeight: 1.6 }}>When a file appears in <strong>⚠ Differences</strong>, click <em>Dismiss</em> to hide it. Dismissed items appear here so you can restore them later.</div>
    </div>
  );

  return (
    <div>
      <div style={{ fontSize: 13, color: '#7f8c8d', marginBottom: 12 }}>
        {ignored.length} ignored discrepanc{ignored.length !== 1 ? 'ies' : 'y'} — click Restore to show again.
      </div>
      {ignored.map(path => (
        <div key={path} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px',
                                  background: '#fff', borderRadius: 6, marginBottom: 6,
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <span style={{ fontFamily: 'monospace', fontSize: 12, flex: 1, color: '#555' }}>{path}</span>
          <button style={{ fontSize: 11, padding: '2px 8px', background: '#eafaf1',
                           border: '1px solid #a9dfbf', borderRadius: 4, cursor: 'pointer', color: '#27ae60' }}
                  onClick={() => unignore(path)}>Restore</button>
        </div>
      ))}
    </div>
  );
}

function FileManagerPage({ initialFilter, initialClientId, initialMappingId, clearFilter }) {
  const api = useApi();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [view, setView] = useState('discrepancies'); // 'discrepancies' | 'all' | 'today'
  const [discrepancies, setDiscrepancies] = useState(null);
  const [files, setFiles] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteFiles, setDeleteFiles] = useState(true);
  const [inventoryAge, setInventoryAge] = useState(null);
  const [lastReported, setLastReported] = useState(null);
  const [sortCol, setSortCol] = useState('path');
  const [sortDir, setSortDir] = useState('asc');
  const filesRequestSeq = useRef(0);

  // Converts a plain "YYYY-MM-DD" (what the date input gives us) into the UTC instant that
  // represents midnight *in the browser's own local timezone* for that calendar day - not
  // midnight UTC, which is what new Date("2026-07-08").toISOString() would silently give you
  // instead, shifting results by a timezone offset's worth of hours near day boundaries.
  const toUtcDayStartIso = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
  };

  useEffect(() => {
    if (initialFilter) { setView(initialFilter); }
  }, [initialFilter]);

  useEffect(() => {
    if (!initialClientId || clients.length === 0) return;
    const client = clients.find(c => c.id === initialClientId);
    if (client) {
      selectClient(client).then(() => {
        if (initialMappingId) {
          // Wait for mappings to load then select
          api.get(`/clients/${initialClientId}`).then(full => {
            const m = full?.mappings?.find(x => x.id === initialMappingId);
            if (m) { setSelectedMapping(m); loadData(initialClientId, initialMappingId, initialFilter || 'discrepancies'); }
          });
        }
      });
      if (clearFilter) clearFilter();
    }
  }, [initialClientId, clients]);

  useEffect(() => {
    api.get('/clients').then(data => setClients(data || []));
  }, []);

  const loadData = async (clientId, mappingId, currentView, opts = {}) => {
    setLoading(true);
    setSelected(new Set());
    try {
      if (currentView === 'discrepancies' || currentView === undefined) {
        const data = await api.get(`/clients/${clientId}/mappings/${mappingId}/manifest/discrepancies`);
        setDiscrepancies(data?.discrepancies || []);
        setInventoryAge(data?.inventoryAge);
        setLastReported(data?.lastReported);
      } else if (currentView === 'all' || currentView === 'today') {
        const params = new URLSearchParams();
        if (opts.q) params.set('q', opts.q);
        if (opts.fromDate) params.set('fromDate', toUtcDayStartIso(opts.fromDate));
        if (opts.toDate) params.set('toDate', toUtcDayStartIso(opts.toDate));
        params.set('sortCol', opts.sortCol || sortCol);
        params.set('sortDir', opts.sortDir || sortDir);
        params.set('pageSize', '1000');
        const mySeq = ++filesRequestSeq.current;
        const data = await api.get(`/clients/${clientId}/mappings/${mappingId}/manifest/search?${params}`);
        // A newer request may have been issued (and possibly already resolved) while this
        // one was in flight — if so, this response is stale and must not overwrite the
        // screen, regardless of which request actually finishes first.
        if (mySeq !== filesRequestSeq.current) return;
        setFiles(data?.files || []);
        setHasMore(!!data?.hasMore);
      } else {
        const data = await api.get(`/clients/${clientId}/mappings/${mappingId}/manifest`);
        setFiles(data || []);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMoreFiles = async () => {
    if (!selectedMapping || files.length === 0) return;
    setLoadingMore(true);
    try {
      const last = files[files.length - 1];
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      if (fromDate) params.set('fromDate', toUtcDayStartIso(fromDate));
      if (toDate) params.set('toDate', toUtcDayStartIso(toDate));
      params.set('sortCol', sortCol);
      params.set('sortDir', sortDir);
      params.set('cursorSortValue', last.sortValue);
      params.set('cursorPath', last.relativePath);
      params.set('pageSize', '1000');
      const data = await api.get(
        `/clients/${selectedClient.id}/mappings/${selectedMapping.id}/manifest/search?${params}`);
      setFiles(f => [...f, ...(data?.files || [])]);
      setHasMore(!!data?.hasMore);
    } finally {
      setLoadingMore(false);
    }
  };

  const selectClient = async (c) => {
    const full = await api.get(`/clients/${c.id}`);
    setSelectedClient(full);
    setSelectedMapping(null);
    setDiscrepancies(null);
    setFiles([]);
  };

  const selectMapping = (m) => {
    setSelectedMapping(m);
    loadData(selectedClient.id, m.id, view);
  };

  const todayLocalStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  };

  const switchView = (v) => {
    setView(v);
    setSelected(new Set());
    setSearch('');
    if (v === 'today') {
      const today = todayLocalStr();
      setFromDate(today); setToDate('');
      if (selectedMapping) loadData(selectedClient.id, selectedMapping.id, v, { fromDate: today });
    } else {
      if (v === 'all') { setFromDate(''); setToDate(''); }
      if (selectedMapping) loadData(selectedClient.id, selectedMapping.id, v);
    }
  };

  // Debounce search box + date range: wait for typing to settle, then re-fetch from the
  // server (resets pagination) rather than filtering something already loaded — this is
  // a real query against the full history now, not just what happened to be in memory.
  useEffect(() => {
    if (!selectedMapping || (view !== 'all' && view !== 'today')) return;
    const t = setTimeout(() => {
      loadData(selectedClient.id, selectedMapping.id, view, { q: search, fromDate, toDate });
    }, 300);
    return () => clearTimeout(t);
  }, [search, fromDate, toDate]);

  useEffect(() => {
    if (!selectedMapping || (view !== 'all' && view !== 'today')) return;
    loadData(selectedClient.id, selectedMapping.id, view, { q: search, fromDate, toDate, sortCol, sortDir });
  }, [sortCol, sortDir]);

  const toggleSelect = (path) => {
    const s = new Set(selected);
    s.has(path) ? s.delete(path) : s.add(path);
    setSelected(s);
  };

  const toggleSelectAll = (items) => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map(f => f.relativePath || f.RelativePath)));
  };

  const handleDelete = async () => {
    if (selected.size === 0) return;
    await api.post(
      `/clients/${selectedClient.id}/mappings/${selectedMapping.id}/manifest/bulk-delete`,
      { paths: [...selected], deleteFiles }
    );
    setConfirmDelete(false);
    setSelected(new Set());
    loadData(selectedClient.id, selectedMapping.id, view);
  };

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1048576) return `${(bytes/1024).toFixed(1)}KB`;
    return `${(bytes/1048576).toFixed(1)}MB`;
  };

  const statusBadge = (status) => {
    const map = {
      ServerOnly: { label: 'Server Only', color: '#e67e22', bg: '#fef5ec' },
      ClientOnly: { label: 'Client Only', color: '#2980b9', bg: '#ebf5fb' },
      PendingDownload: { label: 'Pending Download', color: '#8e44ad', bg: '#f5eef8' },
      HashMismatch: { label: 'Hash Mismatch', color: '#8e44ad', bg: '#f5eef8' },
      Overdue: { label: '⚠ Overdue', color: '#c0392b', bg: '#fdedec' },
      DeliveredButDeleted: { label: 'Delivered — Deleted', color: '#7f8c8d', bg: '#f0f0f0' },
    };
    const s = map[status] || { label: status, color: '#7f8c8d', bg: '#f0f0f0' };
    return (
      <span style={{ background: s.bg, color: s.color, borderRadius: 4,
                     padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
        {s.label}
      </span>
    );
  };

  // Filter for all-files view
  const isDownloadOnly = selectedMapping?.syncDirection === 'DownloadOnly';

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };
  const SortIcon = ({ col }) => sortCol === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ⇅';

  const sortFn = (a, b, getVal) => {
    const av = getVal(a), bv = getVal(b);
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  };
  const filteredDisc = (discrepancies || []).filter(f => {
    const path = f.relativePath || f.RelativePath || '';
    return !search || path.toLowerCase().includes(search.toLowerCase());
  });
  const sortedDisc = [...filteredDisc].sort((a,b) => {
    const ap = a.relativePath||a.RelativePath||'', bp = b.relativePath||b.RelativePath||'';
    if (sortCol==='size') return sortFn(a,b,x=>x.sizeBytes||x.SizeBytes||0);
    if (sortCol==='modified') return sortFn(a,b,x=>x.serverModified||x.clientModified||'');
    return sortFn({v:ap},{v:bp},x=>x.v);
  });

  return (
    <div style={styles.page}>
      <h1 style={styles.pageTitle}>File Manager</h1>

      <div style={styles.twoCol}>
        {/* Left: client + mapping selector */}
        <div style={{ width: 240, flexShrink: 0 }}>
          <div style={styles.sectionHeader}>Clients</div>
          {clients.map(c => (
            <div key={c.id}
                 style={{ ...styles.listItem, ...(selectedClient?.id === c.id ? styles.listItemActive : {}),
                          gap:8, padding:'9px 12px' }}
                 onClick={() => selectClient(c)}>
              <div style={{ width:28, height:28, borderRadius:6, flexShrink:0,
                            background: selectedClient?.id === c.id ? '#bfdbfe' : '#e2e8f0',
                            display:'flex', alignItems:'center', justifyContent:'center',
                            fontSize:11, fontWeight:500, color: selectedClient?.id === c.id ? '#1d4ed8' : '#4a5568' }}>
                {c.name.slice(0,2).toUpperCase()}
              </div>
              <span style={{fontSize:13, color:'#1a202c'}}>{c.name}</span>
            </div>
          ))}
          {selectedClient?.mappings?.length > 0 && (
            <>
              <div style={{ ...styles.sectionHeader, marginTop:16 }}>Mappings</div>
              {selectedClient.mappings.map(m => (
                <div key={m.id}
                     style={{ ...styles.listItem, ...(selectedMapping?.id === m.id ? styles.listItemActive : {}),
                              gap:8, padding:'9px 12px' }}
                     onClick={() => selectMapping(m)}>
                  <i className={`ti ${m.syncDirection === 'DownloadOnly' ? 'ti-download' : 'ti-upload'}`}
                     style={{fontSize:14, color: selectedMapping?.id === m.id ? '#1d4ed8' : '#9ca3af', flexShrink:0}}
                     aria-hidden="true"></i>
                  <span style={{fontSize:13, color:'#1a202c'}}>{m.label}</span>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Right: file view */}
        <div style={{ flex: 1 }}>
          {!selectedMapping ? (
            <div style={{ color: '#7f8c8d', padding: 20 }}>Select a client and mapping to view files.</div>
          ) : (
            <>
              {/* View switcher */}
              <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
                {[
                  { id:'discrepancies', label:'Differences', icon:'ti-alert-triangle' },
                  { id:'all',           label:'All Files',   icon:'ti-files' },
                  { id:'today',         label:'Today',       icon:'ti-calendar-event' },
                  { id:'ignored',       label:'Ignored',     icon:'ti-ban' },
                  { id:'sync-errors',   label:'Sync Errors', icon:'ti-history' },
                  { id:'unc-errors',    label:'UNC Errors',  icon:'ti-x' },
                ].map(v => (
                  <button key={v.id} onClick={() => switchView(v.id)}
                          style={{ padding:'7px 14px', borderRadius:8, cursor:'pointer', fontSize:13, display:'flex',
                                   alignItems:'center', gap:6,
                                   border: view === v.id ? 'none' : '0.5px solid #e2e8f0',
                                   background: view === v.id ? (v.id === 'unc-errors' ? '#fef2f2' : v.id === 'sync-errors' ? '#fffbeb' : '#ebf5fb') : '#fff',
                                   color: view === v.id ? (v.id === 'unc-errors' ? '#991b1b' : v.id === 'sync-errors' ? '#92400e' : '#1d4ed8') : '#6b7280',
                                   fontWeight: view === v.id ? 500 : 400 }}>
                    <i className={`ti ${v.icon}`} style={{fontSize:14}} aria-hidden="true"></i>
                    {v.label}
                  </button>
                ))}
                <div style={{ flex: 1 }} />
                <input style={{ ...styles.input, marginBottom: 0, width: 200 }}
                       placeholder="Search..." value={search}
                       onChange={e => setSearch(e.target.value)} />
                <button style={{ ...styles.btnSm, padding: '6px 12px' }}
                        onClick={() => loadData(selectedClient.id, selectedMapping.id, view)}>
                  ↻
                </button>
              </div>

              {/* Inventory age warning */}
              {view === 'ignored' && <IgnoredList clientId={selectedClient.id} mappingId={selectedMapping.id} api={api} onUnignore={() => loadData(selectedClient.id, selectedMapping.id, 'discrepancies')} />}
              {view === 'unc-errors' && <UncErrorList clientId={selectedClient.id} mappingId={selectedMapping.id} api={api} onAction={() => loadData(selectedClient.id, selectedMapping.id, 'unc-errors')} />}
              {view === 'sync-errors' && <SyncErrorList clientId={selectedClient.id} mappingId={selectedMapping.id} api={api} />}
              {view === 'discrepancies' && lastReported && (
                <div style={{ padding: '8px 12px', borderRadius: 6, marginBottom: 10,
                              background: inventoryAge && inventoryAge > 600 ? '#fef5ec' : '#eafaf1',
                              color: inventoryAge && inventoryAge > 600 ? '#e67e22' : '#27ae60',
                              fontSize: 12 }}>
                  {lastReported
                    ? (isDownloadOnly ? `Last download sync: ${new Date(lastReported).toLocaleString()}` : `Client inventory last reported: ${new Date(lastReported).toLocaleString()}`)
                    : (isDownloadOnly ? 'No download activity yet' : 'No client inventory yet — waiting for next reconciliation cycle')}
                </div>
              )}

              {loading ? <Spinner /> : view === 'ignored' || view === 'unc-errors' || view === 'sync-errors' ? null : view === 'discrepancies' ? (
                // ── Discrepancy view ──────────────────────────────────────────
                <>
                  {sortedDisc.length === 0 ? (
                    <div style={{ padding: 32, textAlign: 'center', color: '#27ae60', fontSize: 16 }}>
                      ✓ No discrepancies found — client and server are in sync
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <span style={{ fontSize: 13, color: '#7f8c8d' }}>
                          {sortedDisc.length} discrepanc{filteredDisc.length !== 1 ? 'ies' : 'y'}
                          {selected.size > 0 && ` · ${selected.size} selected`}
                        </span>
                        {selected.size > 0 && (
                          <button style={{ ...styles.btn, background: '#e74c3c', padding: '6px 12px', fontSize: 12 }}
                                  onClick={() => setConfirmDelete(true)}>
                            Remove {selected.size} from manifest
                          </button>
                        )}
                      </div>
                      <div style={{ background: '#fff', borderRadius: 8, overflow: 'hidden',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead>
                            <tr style={{ background: '#f7f9fc', borderBottom: '1px solid #e0e6ed' }}>
                              <th style={{ padding: '10px 12px', width: 32 }}>
                                <input type="checkbox"
                                       checked={selected.size === sortedDisc.filter(f => f.status === 'ServerOnly').length && sortedDisc.filter(f => f.status === 'ServerOnly').length > 0}
                                       onChange={() => toggleSelectAll(sortedDisc.filter(f => f.status === 'ServerOnly'))} />
                              </th>
                              <th style={{ padding: '10px 12px', textAlign: 'left', cursor:'pointer', userSelect:'none' }} onClick={() => handleSort('path')}>Path<SortIcon col="path" /></th>
                              <th style={{ padding: '10px 12px', textAlign: 'center', width: 130 }}>Status</th>
                              <th style={{ padding: '10px 12px', textAlign: 'right', width: 80, cursor:'pointer', userSelect:'none' }} onClick={() => handleSort('size')}>Size<SortIcon col="size" /></th>
                              <th style={{ padding: '10px 12px', textAlign: 'left', width: 150, cursor:'pointer', userSelect:'none' }} onClick={() => handleSort('modified')}>Modified<SortIcon col="modified" /></th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedDisc.map((f, i) => {
                              const path = f.relativePath || f.RelativePath;
                              const canDelete = f.status === 'ServerOnly';
                              return (
                                <tr key={path}
                                    style={{ background: selected.has(path) ? '#ebf5fb'
                                             : i % 2 === 0 ? '#fff' : '#fafbfc',
                                             borderBottom: '1px solid #f0f0f0',
                                             cursor: canDelete ? 'pointer' : 'default' }}
                                    onClick={() => canDelete && toggleSelect(path)}>
                                  <td style={{ padding: '8px 12px' }}>
                                    {canDelete && (
                                      <input type="checkbox" checked={selected.has(path)}
                                             onChange={() => toggleSelect(path)}
                                             onClick={e => e.stopPropagation()} />
                                    )}
                                  </td>
                                  <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>
                                    {path}
                                  </td>
                                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                    {statusBadge(f.status)}
                                  </td>
                                  <td style={{ padding: '8px 12px', textAlign: 'right', color: '#7f8c8d' }}>
                                    {formatSize(f.sizeBytes || f.SizeBytes)}
                                  </td>
                                  <td style={{ padding: '8px 12px', color: '#7f8c8d', fontSize: 12 }}>
                                    {new Date(f.serverModified || f.clientModified || f.ServerModified || f.ClientModified).toLocaleString()}
                                  </td>
                                  <td style={{ padding: '8px 4px' }}>
                                    {f.status === 'PendingDownload' ? (
                                      <button
                                        title="Mark as received"
                                        style={{ fontSize: 11, padding: '2px 8px', background: '#eafaf1',
                                                 border: '1px solid #a9dfbf', borderRadius: 4, cursor: 'pointer',
                                                 color: '#27ae60' }}
                                        onClick={async e => {
                                          e.stopPropagation();
                                          await api.post('/download/confirm', { filePath: path });
                                          loadData(selectedClient.id, selectedMapping.id, view);
                                        }}>
                                        Mark Received
                                      </button>
                                    ) : (
                                      <button
                                        title="Ignore this discrepancy"
                                        style={{ fontSize: 11, padding: '2px 8px', background: '#ecf0f1',
                                                 border: '1px solid #bdc3c7', borderRadius: 4, cursor: 'pointer',
                                                 color: '#7f8c8d' }}
                                        onClick={async e => {
                                          e.stopPropagation();
                                          await api.post(`/clients/${selectedClient.id}/mappings/${selectedMapping.id}/ignore`,
                                            { filePath: path });
                                          loadData(selectedClient.id, selectedMapping.id, view);
                                        }}>
                                        Dismiss
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ marginTop: 8, fontSize: 12, color: '#7f8c8d' }}>
                        ℹ️ Only <strong>Server Only</strong> entries can be removed from manifest.
                        Client Only files will upload on next reconciliation.
                      </div>
                    </>
                  )}
                </>
              ) : (
                // ── All files / Today view ────────────────────────────────────
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, color: '#7f8c8d' }}>
                      {files.length} file{files.length !== 1 ? 's' : ''}{hasMore ? '+' : ''}
                      {selected.size > 0 && ` · ${selected.size} selected`}
                    </span>
                    <input type="date" value={fromDate}
                           onChange={e => setFromDate(e.target.value)}
                           style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #e0e6ed', borderRadius: 6 }} />
                    <span style={{ fontSize: 12, color: '#7f8c8d' }}>to</span>
                    <input type="date" value={toDate}
                           onChange={e => setToDate(e.target.value)}
                           style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #e0e6ed', borderRadius: 6 }} />
                    {selected.size > 0 && (
                      <button style={{ ...styles.btn, background: '#e74c3c', padding: '6px 12px', fontSize: 12 }}
                              onClick={() => setConfirmDelete(true)}>
                        Delete {selected.size}
                      </button>
                    )}
                  </div>
                  <div style={{ background: '#fff', borderRadius: 8, overflow: 'hidden',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#f7f9fc', borderBottom: '1px solid #e0e6ed' }}>
                          <th style={{ padding: '10px 12px', width: 32 }}>
                            <input type="checkbox"
                                   checked={selected.size === files.length && files.length > 0}
                                   onChange={() => toggleSelectAll(files)} />
                          </th>
                          <th style={{ padding: '10px 12px', textAlign: 'left', cursor:'pointer', userSelect:'none' }} onClick={() => handleSort('path')}>Path<SortIcon col="path" /></th>
                          <th style={{ padding: '10px 12px', textAlign: 'right', width: 80, cursor:'pointer', userSelect:'none' }} onClick={() => handleSort('size')}>Size<SortIcon col="size" /></th>
                          <th style={{ padding: '10px 12px', textAlign: 'center', width: 80 }}>On Disk</th>
                          <th style={{ padding: '10px 12px', textAlign: 'center', width: 100 }}>UNC</th>
                          <th style={{ padding: '10px 12px', textAlign: 'left', width: 150, cursor:'pointer', userSelect:'none' }} onClick={() => handleSort('modified')}>{isDownloadOnly ? 'Received' : 'Modified'}<SortIcon col="modified" /></th>
                        </tr>
                      </thead>
                      <tbody>
                        {files.map((f, i) => (
                          <tr key={f.relativePath}
                              style={{ background: selected.has(f.relativePath) ? '#ebf5fb'
                                       : i % 2 === 0 ? '#fff' : '#fafbfc',
                                       borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                              onClick={() => toggleSelect(f.relativePath)}>
                            <td style={{ padding: '8px 12px' }}>
                              <input type="checkbox" checked={selected.has(f.relativePath)}
                                     onChange={() => toggleSelect(f.relativePath)}
                                     onClick={e => e.stopPropagation()} />
                            </td>
                            <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>
                              {f.relativePath}
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', color: '#7f8c8d' }}>
                              {formatSize(f.sizeBytes)}
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                              <span style={{ color: f.existsOnDisk ? '#27ae60' : '#e74c3c' }}>
                                {f.existsOnDisk ? '✓' : '✗'}
                              </span>
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'center', fontSize: 11 }}>
                              {!f.uncExportEnabled ? <span style={{ color: '#bdc3c7' }}>—</span>
                                : f.uncExported ? <span style={{ color: '#27ae60' }}>✓</span>
                                : f.uncExportError ? <span style={{ color: '#e74c3c' }} title={f.uncExportError}>⚠ {f.uncExportAttempts}x</span>
                                : <span style={{ color: '#f39c12' }}>⏳</span>}
                            </td>
                            <td style={{ padding: '8px 12px', color: '#7f8c8d', fontSize: 12 }}>
                              {isDownloadOnly && f.clientReceivedUtc ? new Date(f.clientReceivedUtc).toLocaleString() : new Date(f.lastModifiedUtc).toLocaleString()}
                            </td>
                            <td style={{ padding: '8px 6px' }}>
                              {selectedMapping?.syncDirection === 'DownloadOnly' && f.clientReceivedUtc && (
                                <RedownloadBtn
                                  clientId={selectedClient.id}
                                  mappingId={selectedMapping.id}
                                  filePath={f.relativePath}
                                  api={api}
                                  onDone={() => loadData(selectedClient.id, selectedMapping.id, view)}
                                />
                              )}
                            </td>
                          </tr>
                        ))}
                        {files.length === 0 && (
                          <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#7f8c8d' }}>
                            No files found.
                          </td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {hasMore && (
                    <div style={{ textAlign: 'center', marginTop: 12 }}>
                      <button style={{ ...styles.btn, padding: '8px 20px', fontSize: 13 }}
                              onClick={loadMoreFiles} disabled={loadingMore}>
                        {loadingMore ? 'Loading…' : 'Load more'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div style={styles.modal}>
          <div style={styles.modalCard}>
            <h3 style={{ marginTop: 0 }}>Confirm Remove from Manifest</h3>
            <p>Remove <strong>{selected.size}</strong> entr{selected.size !== 1 ? 'ies' : 'y'} from the server manifest?</p>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, cursor: 'pointer' }}>
              <input type="checkbox" checked={deleteFiles} onChange={e => setDeleteFiles(e.target.checked)} />
              Also delete the encrypted files from disk
            </label>
            <p style={{ color: '#7f8c8d', fontSize: 13 }}>
              {deleteFiles
                ? 'Encrypted files will be permanently deleted from server storage.'
                : 'Encrypted files stay on disk. Client will NOT re-upload (entries removed from manifest).'}
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={{ ...styles.btn, background: '#e74c3c' }} onClick={handleDelete}>Confirm</button>
              <button style={{ ...styles.btn, background: '#7f8c8d' }} onClick={() => setConfirmDelete(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Users Page ────────────────────────────────────────────────────────────────

function UsersPage() {
  const api = useApi();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [changePw, setChangePw] = useState({}); // {username: newpw}
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = async () => {
    setLoading(true);
    const data = await api.get('/users');
    setUsers(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const flash = (msg, isError) => {
    isError ? setError(msg) : setSuccess(msg);
    setTimeout(() => { setError(''); setSuccess(''); }, 4000);
  };

  const createUser = async () => {
    if (!newUsername || !newPassword) return flash('Username and password required.', true);
    try {
      await api.post('/users', { username: newUsername, password: newPassword });
      setNewUsername(''); setNewPassword('');
      flash('User created.', false);
      load();
    } catch (e) { flash(e.message, true); }
  };

  const changePassword = async (username) => {
    const pw = changePw[username];
    if (!pw || pw.length < 8) return flash('Password must be at least 8 characters.', true);
    try {
      await api.patch(`/users/${username}/password`, { username, newPassword: pw });
      setChangePw(p => ({ ...p, [username]: '' }));
      flash('Password updated.', false);
    } catch (e) { flash(e.message, true); }
  };

  const deleteUser = async (username) => {
    if (!window.confirm(`Delete user "${username}"?`)) return;
    await api.del(`/users/${username}`);
    flash('User deleted.', false);
    load();
  };

  const toggleUser = async (u) => {
    await api.patch(`/users/${u.username}/enabled`, !u.enabled);
    load();
  };

  return (
    <div style={styles.page}>
      <h1 style={styles.pageTitle}>User Management</h1>

      {error && <div style={{ ...styles.error, padding: '10px 14px', borderRadius: 6, background: '#fef5ec', marginBottom: 16 }}>{error}</div>}
      {success && <div style={{ padding: '10px 14px', borderRadius: 6, background: '#eafaf1', color: '#27ae60', marginBottom: 16 }}>{success}</div>}

      <Section title="Local Users">
        {loading ? <Spinner /> : users.map(u => (
          <div key={u.id} style={{ background: '#fff', borderRadius: 8, padding: '12px 16px', marginBottom: 8,
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontWeight: 600, flex: 1 }}>{u.username}</span>
            <span style={{ fontSize: 12, color: u.enabled ? '#27ae60' : '#e74c3c' }}>
              {u.enabled ? 'Active' : 'Disabled'}
            </span>
            <input style={{ ...styles.input, marginBottom: 0, width: 180 }}
                   type="password" placeholder="New password"
                   value={changePw[u.username] || ''}
                   onChange={e => setChangePw(p => ({ ...p, [u.username]: e.target.value }))} />
            <button style={styles.btnSm} onClick={() => changePassword(u.username)}>Change PW</button>
            <button style={{ ...styles.btnSm, background: u.enabled ? '#e67e22' : '#27ae60' }}
                    onClick={() => toggleUser(u)}>{u.enabled ? 'Disable' : 'Enable'}</button>
            <button style={{ ...styles.btnSm, background: '#e74c3c' }}
                    onClick={() => deleteUser(u.username)}>Delete</button>
          </div>
        ))}
      </Section>

      <Section title="Add Local User">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input style={{ ...styles.input, marginBottom: 0, flex: 1 }} placeholder="Username"
                 value={newUsername} onChange={e => setNewUsername(e.target.value)} />
          <input style={{ ...styles.input, marginBottom: 0, flex: 1 }} type="password" placeholder="Password (min 8 chars)"
                 value={newPassword} onChange={e => setNewPassword(e.target.value)}
                 onKeyDown={e => e.key === 'Enter' && createUser()} />
          <button style={styles.btn} onClick={createUser}>Add User</button>
        </div>
      </Section>
    </div>
  );
}


// ── Updates Page ─────────────────────────────────────────────────────────────

// ── Agents Page ──────────────────────────────────────────────────────────────
function AgentsPage() {
  const api = useApi();
  const [agents, setAgents] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configuring, setConfiguring] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  // Form state
  const [clientMode, setClientMode] = useState('new'); // 'new' | 'existing'
  const [selectedClientId, setSelectedClientId] = useState('');
  const [newClientName, setNewClientName] = useState('');
  const [unusedMappings, setUnusedMappings] = useState([]);
  const [loadingMappings, setLoadingMappings] = useState(false);
  const [mappingMode, setMappingMode] = useState('new'); // 'new' | 'existing'
  const [selectedMappingId, setSelectedMappingId] = useState('');
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [newMappingLabel, setNewMappingLabel] = useState('');
  const [direction, setDirection] = useState('UploadOnly');
  const [localFolderPath, setLocalFolderPath] = useState('');
  const [serverUrl, setServerUrl] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([api.get('/agents'), api.get('/clients')]);
      setAgents(a || []);
      setClients(c || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const dismiss = async (id) => { await api.del(`/agents/${id}`); load(); };

  const openConfigure = (agent) => {
    setConfiguring(agent);
    setClientMode('new');
    setSelectedClientId('');
    setNewClientName(agent.machineName);
    setUnusedMappings([]);
    setMappingMode('new');
    setSelectedMappingId('');
    setSelectedMapping(null);
    setNewMappingLabel('Documents');
    setDirection('UploadOnly');
    setLocalFolderPath('');
    setServerUrl('');
    setMsg(null);
  };

  const handleClientChange = async (clientId) => {
    setSelectedClientId(clientId);
    setSelectedMappingId('');
    setSelectedMapping(null);
    setMappingMode('new');
    if (!clientId) { setUnusedMappings([]); return; }
    setLoadingMappings(true);
    try {
      const full = await api.get(`/clients/${clientId}`);
      const mappings = full?.mappings || [];
      // Get dashboard stats to find which mappings have been used
      const dash = await api.get('/dashboard').catch(() => null);
      const stats = dash?.mappingStats || [];
      const usedIds = new Set(
        stats.filter(m => m.clientId === clientId && (m.filesWeek > 0 || m.manifestCount > 0))
             .map(m => m.mappingId)
      );
      // A mapping is unused if it has no manifest entries at all
      const unused = mappings.filter(m => !usedIds.has(m.id));
      setUnusedMappings(unused);
    } catch { setUnusedMappings([]); }
    finally { setLoadingMappings(false); }
  };

  const handleMappingSelect = (mappingId) => {
    setSelectedMappingId(mappingId);
    const m = unusedMappings.find(x => x.id === mappingId);
    setSelectedMapping(m || null);
  };

  // Derived display values for new mapping
  const clientName = clientMode === 'new' ? newClientName : (clients.find(c => c.id === selectedClientId)?.name || '');
  const serverPath = clientName && newMappingLabel ? `${clientName}/${newMappingLabel}` : '';
  const uncPath = clientName && newMappingLabel ? `${clientName}\\${newMappingLabel}` : '';

  const submitConfig = async () => {
    if (!localFolderPath.trim()) { setMsg({ type: 'error', text: 'Local folder path is required' }); return; }
    if (clientMode === 'new' && !newClientName.trim()) { setMsg({ type: 'error', text: 'Client name is required' }); return; }
    if (mappingMode === 'new' && !newMappingLabel.trim()) { setMsg({ type: 'error', text: 'Mapping label is required' }); return; }
    if (mappingMode === 'existing' && !selectedMappingId) { setMsg({ type: 'error', text: 'Please select a mapping' }); return; }

    setSaving(true);
    try {
      const payload = {
        clientMode,
        clientId: clientMode === 'existing' ? selectedClientId : null,
        clientName: clientMode === 'new' ? newClientName : null,
        mappingMode,
        mappingId: mappingMode === 'existing' ? selectedMappingId : null,
        mappingLabel: mappingMode === 'new' ? newMappingLabel : null,
        direction: mappingMode === 'new' ? direction : null,
        localFolderPath,
        serverUrl
      };
      await api.post(`/agents/${configuring.id}/configure`, payload);
      setMsg({ type: 'success', text: 'Config pushed — agent will self-configure within 30 seconds.' });
      setTimeout(() => { setConfiguring(null); load(); }, 2000);
    } catch { setMsg({ type: 'error', text: 'Failed to push config' }); }
    finally { setSaving(false); }
  };

  const ago = (utc) => {
    const s = Math.floor((Date.now() - new Date(utc).getTime()) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s/60)}m ago`;
    if (s < 86400) return `${Math.floor(s/3600)}h ago`;
    return `${Math.floor(s/86400)}d ago`;
  };

  const inputStyle = { ...styles.input, marginBottom: 0 };
  const labelStyle = { fontSize: 13, color: '#4a5568', display: 'block', marginBottom: 4 };
  const roStyle = { ...inputStyle, background: '#f7fafc', color: '#718096', cursor: 'default' };

  return (
    <div style={styles.page}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <h1 style={styles.pageTitle}>Agents</h1>
        <button style={{ ...styles.btnSm, background:'#f8fafc', color:'#374151', border:'0.5px solid #e2e8f0' }}
                onClick={load}>Refresh</button>
      </div>

      {loading ? <Spinner /> : agents.length === 0 ? (
        <div style={{ background:'#fff', borderRadius:12, border:'0.5px solid #e2e8f0', padding:40, textAlign:'center', color:'#718096' }}>
          <div style={{ fontSize:32, marginBottom:12 }}>🖥</div>
          <div style={{ fontSize:15, fontWeight:500, marginBottom:8 }}>No agents registered</div>
          <div style={{ fontSize:13 }}>Install ADTSync with a pre-configured server URL.<br/>The agent will appear here automatically within 30 seconds.</div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {agents.map(a => (
            <div key={a.id} style={{ background:'#fff', borderRadius:12, border:'0.5px solid #e2e8f0',
                                      padding:'16px 20px', display:'flex', alignItems:'center', gap:16 }}>
              <div style={{ width:44, height:44, borderRadius:10, background:'#f0f4f8',
                            display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>🖥</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                  <span style={{ fontSize:15, fontWeight:500, color:'#1a202c' }}>{a.machineName}</span>
                  <span style={{ fontSize:11, padding:'2px 8px', borderRadius:99, fontWeight:500,
                                 background: a.status==='Configured'?'#dcfce7':a.status==='Configuring'?'#fef3c7':'#dbeafe',
                                 color: a.status==='Configured'?'#166534':a.status==='Configuring'?'#92400e':'#1d4ed8' }}>
                    {a.status}
                  </span>
                </div>
                <div style={{ fontSize:12, color:'#718096', display:'flex', gap:16, flexWrap:'wrap' }}>
                  <span>{a.ipAddress}</span>
                  <span>{a.osVersion}</span>
                  <span>v{a.agentVersion}</span>
                  <span>Last seen {ago(a.lastSeenUtc)}</span>
                </div>
              </div>
              <div style={{ display:'flex', gap:8, flexShrink:0, alignItems:'center' }}>
                {a.status === 'Pending' && (
                  <button style={{ ...styles.btn, padding:'7px 16px', fontSize:13 }}
                          onClick={() => openConfigure(a)}>Configure</button>
                )}
                {a.status === 'Configuring' && <span style={{ fontSize:12, color:'#e67e22' }}>Waiting for agent...</span>}
                {a.status === 'Configured' && <span style={{ fontSize:12, color:'#27ae60' }}>Self-configured</span>}
                <button style={{ ...styles.btnSm, background:'#fef2f2', color:'#991b1b', border:'0.5px solid #fca5a5' }}
                        onClick={() => dismiss(a.id)}>Dismiss</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {configuring && (
        <div style={styles.modal}>
          <div style={{ ...styles.modalCard, width:560, maxHeight:'90vh', overflowY:'auto' }}>
            <h2 style={{ fontSize:17, fontWeight:500, marginBottom:4 }}>Configure Agent</h2>
            <p style={{ fontSize:13, color:'#718096', marginBottom:20 }}>
              Pushing config to <strong>{configuring.machineName}</strong> ({configuring.ipAddress}).
            </p>

            {msg && (
              <div style={{ padding:'10px 14px', borderRadius:8, marginBottom:16, fontSize:13,
                            background: msg.type==='error'?'#fef2f2':'#f0fdf4',
                            color: msg.type==='error'?'#991b1b':'#166534' }}>
                {msg.text}
              </div>
            )}

            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

              {/* ── Client ── */}
              <div style={{ background:'#f7fafc', borderRadius:8, padding:14, border:'0.5px solid #e2e8f0' }}>
                <div style={{ fontSize:12, fontWeight:500, textTransform:'uppercase', letterSpacing:'0.05em', color:'#718096', marginBottom:10 }}>Client</div>
                <div style={{ display:'flex', gap:8, marginBottom:10 }}>
                  <button onClick={() => setClientMode('new')}
                          style={{ flex:1, padding:'7px', borderRadius:8, border:`1.5px solid ${clientMode==='new'?'#3182ce':'#e2e8f0'}`,
                                   background: clientMode==='new'?'#ebf5fb':'#fff', color: clientMode==='new'?'#1d4ed8':'#374151',
                                   cursor:'pointer', fontSize:13, fontWeight: clientMode==='new'?500:400 }}>
                    + Create new
                  </button>
                  <button onClick={() => setClientMode('existing')}
                          style={{ flex:1, padding:'7px', borderRadius:8, border:`1.5px solid ${clientMode==='existing'?'#3182ce':'#e2e8f0'}`,
                                   background: clientMode==='existing'?'#ebf5fb':'#fff', color: clientMode==='existing'?'#1d4ed8':'#374151',
                                   cursor:'pointer', fontSize:13, fontWeight: clientMode==='existing'?500:400 }}>
                    Use existing
                  </button>
                </div>
                {clientMode === 'new' ? (
                  <div>
                    <label style={labelStyle}>Client Name</label>
                    <input style={inputStyle} value={newClientName}
                           onChange={e => setNewClientName(e.target.value)}
                           placeholder="e.g. Payne Auto Group" />
                  </div>
                ) : (
                  <div>
                    <label style={labelStyle}>Select Client</label>
                    <select style={{ ...inputStyle, padding:'8px 10px' }}
                            value={selectedClientId}
                            onChange={e => handleClientChange(e.target.value)}>
                      <option value="">-- Select a client --</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                )}
              </div>

              {/* ── Mapping ── */}
              <div style={{ background:'#f7fafc', borderRadius:8, padding:14, border:'0.5px solid #e2e8f0' }}>
                <div style={{ fontSize:12, fontWeight:500, textTransform:'uppercase', letterSpacing:'0.05em', color:'#718096', marginBottom:10 }}>Mapping</div>

                {clientMode === 'existing' && selectedClientId && (
                  <div style={{ display:'flex', gap:8, marginBottom:10 }}>
                    <button onClick={() => setMappingMode('existing')}
                            style={{ flex:1, padding:'7px', borderRadius:8, border:`1.5px solid ${mappingMode==='existing'?'#3182ce':'#e2e8f0'}`,
                                     background: mappingMode==='existing'?'#ebf5fb':'#fff', color: mappingMode==='existing'?'#1d4ed8':'#374151',
                                     cursor:'pointer', fontSize:13, fontWeight: mappingMode==='existing'?500:400 }}>
                      Use unused mapping
                    </button>
                    <button onClick={() => setMappingMode('new')}
                            style={{ flex:1, padding:'7px', borderRadius:8, border:`1.5px solid ${mappingMode==='new'?'#3182ce':'#e2e8f0'}`,
                                     background: mappingMode==='new'?'#ebf5fb':'#fff', color: mappingMode==='new'?'#1d4ed8':'#374151',
                                     cursor:'pointer', fontSize:13, fontWeight: mappingMode==='new'?500:400 }}>
                      + Create new mapping
                    </button>
                  </div>
                )}

                {(clientMode === 'existing' && selectedClientId && mappingMode === 'existing') ? (
                  <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                    <div>
                      <label style={labelStyle}>Unused Mappings</label>
                      {loadingMappings ? <div style={{ fontSize:13, color:'#718096' }}>Loading...</div> :
                       unusedMappings.length === 0 ? (
                        <div style={{ fontSize:13, color:'#e67e22', padding:'8px 10px', background:'#fef3c7', borderRadius:6 }}>
                          No unused mappings found for this client. Create a new one instead.
                        </div>
                       ) : (
                        <select style={{ ...inputStyle, padding:'8px 10px' }}
                                value={selectedMappingId}
                                onChange={e => handleMappingSelect(e.target.value)}>
                          <option value="">-- Select a mapping --</option>
                          {unusedMappings.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                        </select>
                       )}
                    </div>
                    {selectedMapping && (
                      <div style={{ display:'flex', flexDirection:'column', gap:8, padding:10, background:'#fff', borderRadius:6, border:'0.5px solid #e2e8f0' }}>
                        <div style={{ display:'flex', gap:16, fontSize:12 }}>
                          <span><strong>Direction:</strong> {selectedMapping.syncDirection === 'DownloadOnly' ? '⬇ Download' : '⬆ Upload'}</span>
                          <span><strong>Server path:</strong> {selectedMapping.serverFolderPath}</span>
                        </div>
                        {selectedMapping.uncExportEnabled && (
                          <div style={{ fontSize:12 }}>
                            <strong>UNC:</strong> {selectedMapping.uncPath || '(default path)'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                    <div style={{ display:'flex', gap:10 }}>
                      <div style={{ flex:1 }}>
                        <label style={labelStyle}>Mapping Label</label>
                        <input style={inputStyle} value={newMappingLabel}
                               onChange={e => setNewMappingLabel(e.target.value)}
                               placeholder="e.g. Documents" />
                      </div>
                      <div style={{ flex:1 }}>
                        <label style={labelStyle}>Direction</label>
                        <select style={{ ...inputStyle, padding:'8px 10px' }}
                                value={direction} onChange={e => setDirection(e.target.value)}>
                          <option value="UploadOnly">⬆ Upload</option>
                          <option value="DownloadOnly">⬇ Download</option>
                        </select>
                      </div>
                    </div>
                    {serverPath && (
                      <div style={{ display:'flex', gap:10 }}>
                        <div style={{ flex:1 }}>
                          <label style={labelStyle}>Server Path (auto)</label>
                          <input style={roStyle} value={serverPath} readOnly />
                        </div>
                        <div style={{ flex:1 }}>
                          <label style={labelStyle}>UNC Path (auto)</label>
                          <input style={roStyle} value={uncPath} readOnly />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ── Local Folder ── */}
              <div>
                <label style={labelStyle}>Local Folder Path <span style={{ color:'#e53e3e' }}>*</span></label>
                <input style={inputStyle} value={localFolderPath}
                       onChange={e => setLocalFolderPath(e.target.value)}
                       placeholder="C:\Users\username\Documents" />
                <div style={{ fontSize:11, color:'#718096', marginTop:4 }}>Full path to the folder on the remote machine.</div>
              </div>

              {/* ── Server URL Override ── */}
              <div>
                <label style={labelStyle}>Server URL Override <span style={{ fontSize:11, color:'#718096' }}>(optional)</span></label>
                <input style={inputStyle} value={serverUrl}
                       onChange={e => setServerUrl(e.target.value)}
                       placeholder="https://your-server.com" />
              </div>
            </div>

            <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:24 }}>
              <button style={{ ...styles.btnSm, background:'#f8fafc', color:'#374151', border:'0.5px solid #e2e8f0', padding:'8px 16px' }}
                      onClick={() => setConfiguring(null)}>Cancel</button>
              <button style={{ ...styles.btn, opacity: saving ? 0.7 : 1 }}
                      onClick={submitConfig} disabled={saving}>
                {saving ? 'Pushing...' : 'Push Config'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function UpdatesPage() {
  const api = useApi();
  const [info, setInfo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [version, setVersion] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);

  const load = async () => {
    try { setInfo(await api.get('/update/info')); } catch (e) {}
  };

  useEffect(() => { load(); }, []);

  const publish = async () => {
    if (!file || !version) return setResult({ ok: false, msg: 'Version and MSI file required.' });
    setUploading(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('version', version);
      form.append('releaseNotes', notes);
      // Get token from context via the useAuth hook value stored in closure
      const allKeys = Object.keys(localStorage);
      const tokenKey = allKeys.find(k => k.includes('token')) || 'adtsync_token';
      const tok = localStorage.getItem(tokenKey);
      console.log('Publish token key:', tokenKey, 'token exists:', !!tok);
      const resp = await fetch('/api/update/publish', {
        method: 'POST',
        headers: { Authorization: `Bearer ${tok}` },
        body: form
      });
      const respText = await resp.text();
      console.log('Publish response:', resp.status, respText);
      if (resp.ok) {
        setResult({ ok: true, msg: `v${version} published successfully.` });
        setVersion(''); setNotes(''); setFile(null);
        load();
      } else {
        setResult({ ok: false, msg: `${resp.status}: ${respText || 'Unknown error'}` });
      }
    } catch (e) { setResult({ ok: false, msg: e.message }); }
    setUploading(false);
  };

  return (
    <div style={styles.page}>
      <h1 style={styles.pageTitle}>Client Auto-Update</h1>

      <Section title="Current Published Version">
        {info ? (
          info.version && info.version !== '0.0.0' ? (
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#2c3e50' }}>v{info.version}</div>
              <div style={{ fontSize: 13, color: '#7f8c8d', marginTop: 4 }}>
                Published {info.publishedUtc ? new Date(info.publishedUtc).toLocaleString() : 'unknown'}
              </div>
              {info.releaseNotes && <div style={{ marginTop: 8, fontSize: 13 }}>{info.releaseNotes}</div>}
              <div style={{ marginTop: 8, fontSize: 11, fontFamily: 'monospace', color: '#aaa',
                            wordBreak: 'break-all' }}>SHA-256: {info.hash}</div>
            </div>
          ) : <div style={{ color: '#e67e22' }}>No update published yet.</div>
        ) : <Spinner />}
      </Section>

      <Section title="Publish New Version">
        <p style={{ fontSize: 13, color: '#7f8c8d', marginTop: 0 }}>
          Upload a new MSI. Clients will download and install it automatically within 4 hours.
          The SHA-256 hash is computed automatically — clients verify it before installing.
        </p>
        <Field label="Version"><input style={styles.input} value={version}
               onChange={e => setVersion(e.target.value)} placeholder="1.2.3" /></Field>
        <Field label="Release Notes"><textarea style={{ ...styles.input, height: 60 }}
               value={notes} onChange={e => setNotes(e.target.value)}
               placeholder="What changed in this version..." /></Field>
        <Field label="MSI File">
          <input type="file" accept=".msi" onChange={e => setFile(e.target.files[0])} />
          {file && <span style={{ fontSize: 12, color: '#27ae60', marginLeft: 8 }}>✓ {file.name}</span>}
        </Field>
        {result && (
          <div style={{ marginLeft: 168, padding: '6px 12px', borderRadius: 6, fontSize: 13,
                        background: result.ok ? '#eafaf1' : '#fef5ec',
                        color: result.ok ? '#27ae60' : '#e74c3c' }}>
            {result.ok ? '✓' : '✗'} {result.msg}
          </div>
        )}
        <div style={{ marginLeft: 168, marginTop: 8 }}>
          <button style={styles.btn} onClick={publish} disabled={uploading}>
            {uploading ? 'Publishing...' : '🚀 Publish Update'}
          </button>
        </div>
      </Section>
    </div>
  );
}

export default function Root() {
  return <AuthProvider><App /></AuthProvider>;
}

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  // ── Login ──
  loginWrap: { display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh', background:'#f0f4f8' },
  loginCard: { background:'#fff', borderRadius:16, padding:40, width:380, border:'0.5px solid #e2e8f0' },
  loginLogo: { width:160, marginBottom:20, display:'block' },
  loginTitle: { marginBottom:24, color:'#1a202c', fontSize:20, fontWeight:500 },

  // ── Shell ──
  shell: { display:'flex', minHeight:'100vh', fontFamily:'system-ui,-apple-system,sans-serif', color:'#1a202c', background:'#f7f9fc' },
  sidebar: { width:220, background:'#1a202c', display:'flex', flexDirection:'column', padding:'16px 0', flexShrink:0 },
  sidebarLogo: { width:130, margin:'4px auto 6px', display:'block', filter:'invert(1) brightness(0.9)' },
  sidebarTitle: { color:'#4a5568', textAlign:'center', fontWeight:500, fontSize:11, marginBottom:24, letterSpacing:'0.08em', textTransform:'uppercase' },
  navItem: { padding:'9px 16px', color:'#a0aec0', cursor:'pointer', borderRadius:8, margin:'1px 8px',
             fontSize:13, fontWeight:400, display:'flex', alignItems:'center', transition:'background 0.12s, color 0.12s' },
  navActive: { background:'#2d3748', color:'#fff' },
  navUser: { padding:'8px 16px', color:'#4a5568', fontSize:12, display:'flex', alignItems:'center' },
  sidebarFooter: { marginTop:'auto', borderTop:'0.5px solid #2d3748', paddingTop:8 },
  main: { flex:1, background:'#f7f9fc', overflowY:'auto' },

  // ── Pages ──
  page: { padding:'28px 32px', maxWidth:1140 },
  pageTitle: { fontSize:20, fontWeight:500, marginBottom:20, color:'#1a202c' },

  // ── Two-col layout ──
  twoCol: { display:'flex', gap:20 },
  listPanel: { width:260, flexShrink:0, display:'flex', flexDirection:'column', gap:4 },
  detailPanel: { flex:1, background:'#fff', borderRadius:12, padding:24, border:'0.5px solid #e2e8f0' },
  detailHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 },

  // ── List items ──
  listItem: { padding:'10px 12px', background:'#fff', borderRadius:8, cursor:'pointer',
              display:'flex', justifyContent:'space-between', alignItems:'center',
              border:'0.5px solid #e2e8f0', fontSize:13 },
  listItemActive: { background:'#ebf5fb', borderColor:'#90cdf4', borderLeft:'2px solid #3182ce' },

  // ── Cards ──
  mappingCard: { background:'#f7fafc', borderRadius:10, padding:14, marginBottom:8, border:'0.5px solid #e2e8f0' },
  formCard: { background:'#f7fafc', borderRadius:10, padding:20, marginTop:16, border:'0.5px solid #e2e8f0' },

  // ── Misc ──
  addRow: { display:'flex', marginBottom:12, alignItems:'center', gap:8 },
  sectionHeader: { fontWeight:500, fontSize:11, textTransform:'uppercase', letterSpacing:'0.06em',
                   color:'#718096', marginBottom:10, borderBottom:'0.5px solid #e2e8f0', paddingBottom:6 },
  fieldLabel: { width:220, fontSize:13, color:'#4a5568', flexShrink:0 },
  input: { width:'100%', padding:'8px 10px', borderRadius:8, border:'0.5px solid #cbd5e0',
           fontSize:13, marginBottom:6, boxSizing:'border-box', background:'#fff', color:'#1a202c',
           outline:'none' },
  btn: { background:'#3182ce', color:'#fff', border:'none', borderRadius:8,
         padding:'9px 20px', cursor:'pointer', fontSize:13, fontWeight:500 },
  btnSm: { background:'#3182ce', color:'#fff', border:'none', borderRadius:6,
           padding:'5px 12px', cursor:'pointer', fontSize:12, fontWeight:500 },
  small: { fontSize:12, color:'#718096', marginTop:2 },
  error: { color:'#e53e3e', fontSize:13, marginBottom:10 },
  modal: { position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex',
           alignItems:'center', justifyContent:'center', zIndex:1000 },
  modalCard: { background:'#fff', borderRadius:16, padding:32, width:500, border:'0.5px solid #e2e8f0' },
};
